import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ColDef, FirstDataRenderedEvent, GridOptions } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { PanelBaseComponent } from '../panels/panel-base.component';
import { UserSettingsService } from '../user-settings.service';
import { MessageBusService } from '../message-bus.service';
import { EventStreamConnectionChangedUIMessage } from '../ui-messages/event-stream-connection-changed-message-ui-message.interface';
import { ShellClientService } from '../shell-communication/shell-client.service';
import { AG_SYMBOL_COL_MIN_WIDTH, centeredColumnDef, defaultMoneyCellDefinition } from '../ag-grid-contrib';
import { GenericConfirmationDialogComponent } from '../generic-confirmation-dialog/generic-confirmation-dialog.component';
import { CollarsBackdateDialogComponent, CollarsBackdateConfig } from './backdate/collars-backdate-dialog.component';
import { CollarsMakeTransactionDialogComponent } from './after-state/collars-make-transaction-dialog.component';
import { CollarsApplyAfterStateDialogComponent } from './after-state/collars-apply-after-state-dialog.component';
import { CollarsAfterStateService } from './after-state/collars-after-state.service';
import { CollarsTransactionDiffService } from './after-state/collars-transaction-diff.service';
import { CollarsComboSettingsService } from './collars-combo-settings.service';
import { CollarsQgCursorService } from './collars-qg-cursor.service';
import { CollarsAnalysisLockService } from './collars-analysis-lock.service';
import {
   ANALYSIS_SESSION_CHANGED_MESSAGE_TYPE, ANALYSIS_SESSION_TOUCHED_MESSAGE_TYPE, AnalysisComboStateDto, AnalysisPendingBlockDto, AnalysisSessionChangedDto, AnalysisSessionTouchedUIMessage, RevokeAnalysisBlocks
} from '../shell-communication/analysis-session-operations';
import { COLLARS_SELECT_COMBO_REQUEST_TOPIC, OPEN_SUBJECT_REQUEST_TOPIC, OpenSubjectRequest } from '../action-board/action-board.model';
import { CollarsMakeTransactionConfig, CollarsApplyAfterStateConfig } from './after-state/collars-after-state.models';
import { CollarsAcquireDialogComponent } from './after-state/collars-acquire-dialog.component';
import { CollarsSessionHistoryScope } from './session-history/collars-session-history-scope';
import {
   CollarsBucketDto,
   CollarsTerminalDto,
   CollarsPositionDto,
   CollarsLedgerDto,
   CollarsPositionChangedDto,
   CollarsLedgerChangedDto,
   CollarsSessionEndedDto,
   CreateCollarsCombo,
   CreateCollarsGroup,
   CreateCollarsPortfolio,
   DeleteCollarsBucket,
   GetCollarsAccessibleTerminals,
   ListCollarsBuckets,
   ListCollarsPositions,
   GetCollarsLedger,
   SetCollarsComboSettings,
   SetCollarsInterestSettings,
   ChargeCollarsInterest,
   PreviewCollarsInterestCharge,
   CollarsInterestChargePreviewDto,
   TransferCollarsPosition,
   TransferCollarsGroup,
   SettleCollarsOptionExpiry,
   PreviewCollarsOptionExpiry,
   CollarsOptionExpiryPreviewDto,
   ArchiveCollarsPosition,
   ArchiveCollarsBucket,
   OverrideCollarsPosition,
   COLLARS_TXN_LABELS,
   RecordCollarsAccountCash
} from '../shell-communication/collars-operations';
import { parseOptionTicker } from '../options-common/options.model';
import { TradingInstrumentDisplayNameService } from '../trading-instruments/trading-instrument-display-name.service';
import { TimestampsService } from '../timestamps.service';
import { TIME_ZONE_FAMILIES } from '../timezones/time-zones.const';
import { daysToExpiration, makeCollarsOptionDisplayName, makeCollarsLedgerTimestamp, groupTimezoneFamilies, isCashSettledOptionTicker } from '../utils';
import { LoggerService } from '../logging/logger-factory.service';
import { Logger } from '../logging/logger.interface';

const LEVEL_PORTFOLIO = 0;
const LEVEL_COMBO = 1;
const LEVEL_GROUP = 2;

const KIND_ASSET = 0;
const KIND_OPTION = 1;

const TXN_INTEREST = 5;      // CollarsTransactionType.Interest
const TXN_OVERRIDE = 14;     // CollarsTransactionType.Override (highlighted orange, ETS-CORE parity)

function compareDisplayNames(a: string, b: string): number {
   return (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });
}

// HTML-escape for plain-string ag-Grid cell renderers (bucket names are user-entered text).
function escapeHtml(value: any): string {
   return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
}

interface BucketRow extends CollarsBucketDto {
   terminalName: string;
   userName: string;
}

/** A Portfolio Items row projected from an exported analysis change, not a booked position. */
interface PendingFillRow extends CollarsPositionDto {
   isPendingFill: boolean;
   isPendingFillPlaceholder?: boolean;
   analysisBlockId?: string;
}

/**
 * Collars panel (CR-20260529-001). M2 adds the money on top of the M1 bucket tree: each bucket
 * carries Session + Accumulated P&L (rolled up client-side from positions, kept live by
 * CollarsPositionChangedDto pushes), the Portfolio Items tab shows the selected bucket's positions,
 * the Ledger tab shows its transactions, and the Make Transaction dialog records fills (enter-only)
 * into the selected scope. Grid/dialog patterns are re-derived from the ETS-CORE
 * panels (reference only — no ETS-CORE imports).
 */
@Component({
   selector: 'ets-collars',
   templateUrl: './collars.component.html',
   styleUrls: ['./collars.component.scss'],
   // Collapse the whole host when inactive (same pattern as PriceChartPanelComponent). Without this the
   // host keeps height:100% even though .panel-container is hidden, so a panel added AFTER Collars is
   // pushed below it and clipped by .ets-workspace-content { overflow:hidden }.
   host: { '[class.ets-hidden]': '!isActive' },
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollarsComponent extends PanelBaseComponent {

   constructor(
      protected readonly _changeDetector: ChangeDetectorRef,
      protected readonly _userSettingsService: UserSettingsService,
      protected readonly _messageBus: MessageBusService,
      private readonly _shellClient: ShellClientService,
      private readonly _toastr: ToastrService,
      private readonly _displayNames: TradingInstrumentDisplayNameService,
      private readonly _timestamps: TimestampsService,
      private readonly _afterStateService: CollarsAfterStateService,
      private readonly _transactionDiffService: CollarsTransactionDiffService,
      private readonly _comboSettings: CollarsComboSettingsService,
      private readonly _qgCursor: CollarsQgCursorService,
      private readonly _analysisLocks: CollarsAnalysisLockService,
      loggerService: LoggerService
   ) {
      super(_changeDetector, _userSettingsService, _messageBus);
      this._logger = loggerService.createLogger('CollarsComponent');
   }

   private readonly _logger: Logger;

   private _buckets: CollarsBucketDto[] = [];
   // Combos under a live Portfolio Hedging Analysis session (CR-20260930-001): their design actions are
   // DISABLED in the context menus (owner 2026-09-30: never let the trader walk a chain that ends in a
   // refusal); Apply After State stays clickable and explains itself. Refreshed on every reload AND on
   // every AnalysisSessionChangedDto push (M2 smoke: the lock must follow the analysis live).
   private _lockedComboIds = new Set<string>();
   // The analysis state per combo (M2): the pending blocks (exported by a live session, or orphaned by a
   // dead one) give the GREEN EARMARK on their groups (cascading up, the same sign as a saved After
   // State) and the "Clear After State" item; each block carries its session id for the revoke.
   private _analysisByCombo: { [comboId: string]: AnalysisComboStateDto } = {};
   // Group id -> the pending analysis blocks that book into it (from the blocks' changes).
   private _analysisPendingByGroup: { [groupId: string]: AnalysisPendingBlockDto[] } = {};
   private _terminals: CollarsTerminalDto[] = [];
   private _positions: CollarsPositionDto[] = [];
   private _ledger: CollarsLedgerDto[] = [];
   private _roll: { [bucketId: string]: { session: number; accum: number } } = {};
   // Frozen totals of ARCHIVED positions, keyed by bucket — an archived bucket's row shows these
   // (owner decision 2026-06-10: archived buckets display their P&L); active buckets keep _roll.
   private _archivedRoll: { [bucketId: string]: { session: number; accum: number } } = {};

   // Bucket-archiving visibility toggle (owner decision 2026-06-10): archived buckets are HIDDEN by
   // default; the right-click "Show Archived Buckets" flips this. In-memory only — panel lifecycle,
   // never persisted (resets to hidden on every panel open).
   showArchivedBuckets = false;

   private _selectedPortfolioId: string = null;
   private _selectedComboId: string = null;
   private _selectedGroupId: string = null;

   // After-state indicator set, memoized ONCE per data-load pass (recomputed in applyData()), NOT per cell
   // render. The bucket grids' Name badge renderer runs for every visible row on every refresh; reading user
   // settings (listAfterStateGroupIds) there would re-enumerate settings per cell. Compute it once here and
   // let bucketHasAfterState() read this snapshot. Owner-session only (a non-owner resolves an empty set).
   private _afterStateGroupIds: { [groupId: string]: true } = {};

   // Tracks whether the positions grid currently shows an "Active Positions" section. On the
   // absent -> present transition (initial load, or the FIRST active position arriving later) the
   // section auto-expands (owner, 2026-06-12); while it stays present a user's manual collapse holds.
   private _activeSectionWasPresent = false;
   // Pending Fills is a permanent first section while a bucket is selected. Its placeholder row keeps
   // the header visible when empty; collapse it again when its last fill is acquired or cleared.
   private _pendingFillsWerePresent = false;

   // A first render or completed fill may arrive while Portfolio Items is hidden.
   private _positionsAutoSizePending = false;

   private readonly _unsub = new Subject<void>();

   portfoliosGrid: GridOptions;
   combosGrid: GridOptions;
   groupsGrid: GridOptions;
   positionsGrid: GridOptions;
   ledgerGrid: GridOptions;

   sectionSizes = { top: 55, bottom: 45 };

   tabs = [{ text: 'Portfolio Items' }, { text: 'Ledger' }];
   selectedTabIndex = 0;

   // Create dialogs (custom dx-popup, replacing the native prompt).
   comboTemplates = [
      { key: 'HedgedPortfolio', text: 'Hedged Portfolio' },
      { key: 'ReversedHedgedPortfolio', text: 'Reversed Hedged Portfolio' }
   ];
   portfolioDialog = { visible: false, name: '', terminalId: null as string };
   comboDialog = { visible: false, name: '', template: 'HedgedPortfolio', portfolioBucketId: null as string, terminalId: null as string, defaultQty: 1, startingCapital: null as number };
   groupDialog = { visible: false, name: '', comboBucketId: null as string, terminalId: null as string };

   // Set/change a combo's settings (CR-20260611-001): Default Qty (the Make-Transaction totals
   // normalizer) + auto-expire-at-reset. Backend-persisted on the bucket row (SetCollarsComboSettings).
   comboSettingsDialog = { visible: false, comboBucketId: null as string, comboName: '', terminalId: null as string, qty: 1, autoExpire: false, startingCapital: null as number, hasStartingCapital: false, accountValue: 0, freeCash: 0 };
   accountCashDialog = { visible: false, kind: 1, amount: null as number, comboBucketId: null as string, terminalId: null as string };

   // Move (transfer) dialog: relocate a position — or a whole group's positions — to another bucket
   // under a P&L policy. Same-terminal only; the target cascade (portfolio→combo→group) is built from
   // the loaded bucket tree. `scope` discriminates a single-position move from a whole-group move.
   moveDialog = {
      visible: false, scope: 'position' as ('position' | 'group'), title: '',
      sourceId: null as string,        // positionId (scope=position) or groupBucketId (scope=group)
      sourceGroupId: null as string,   // the source group, excluded from the target list
      terminalId: null as string,
      mode: 1,                         // CollarsTransferMode: 1 = With P&L, 2 = Without P&L
      targetPortfolioId: null as string, targetComboId: null as string, targetGroupId: null as string
   };
   transferModes = [{ id: 1, text: 'With P&L' }, { id: 2, text: 'Without P&L' }];

   // Override dialog: manually correct a position's quantity and/or average price (enter-only). Either field
   // is optional — clear a box to leave that value unchanged. Pre-filled with the position's current values.
   overrideDialog = {
      visible: false, title: '', terminalId: null as string, positionId: null as string, groupId: null as string,
      symbol: '', netPosition: null as number, avgPx: null as number
   };

   // Settle-at-expiry dialog (manual, enter-only). The OUTCOME is DERIVED from the settlement level vs the
   // strike (CR-20260611-001) — never chosen. On open the backend resolves the settlement price for the
   // expiration day and returns moneyness + the derived outcome + a plain description (the `preview*` fields
   // below); the user can override the level, which re-derives. Worthless closes the option leg only;
   // in-the-money physical spawns the underlying at the strike; an index option cash-settles.
   settleDialog = {
      visible: false, optionPositionId: null as string, terminalId: null as string,
      optionName: '', net: 0, cashSettled: false, strike: null as number, optionType: '', expiration: '',
      settlementPrice: null as number,
      loading: false, levelResolved: false, levelSource: '', settleTimeUtc: null as Date,
      isInTheMoney: false, intrinsic: null as number, moneynessLabel: '',
      outcomeLabel: '', realizedPnL: null as number, premiumAmount: null as number,
      spawnsUnderlying: false, underlyingCode: '', underlyingQty: 0, underlyingSide: '',
      description: ''   // load-failure message only (set in loadExpiryPreview catch); not the outcome note anymore
   };
   private _expiryPreviewTimer: any = null;

   // Interest settings dialog (WB-17; redesigned 2026-06-03): borrowed amount + rate + days-in-year +
   // the charge schedule (interval / weekday / time-of-day / timezone). rate is the DECIMAL fraction
   // (the dialog's % mask shows it as a percent); the country is derived from the chosen timezone.
   interestDialog = {
      visible: false, groupBucketId: null as string, terminalId: null as string, groupName: '',
      borrowed: null as number, rate: null as number, daysInYear: 365,
      chargeInterval: 0, chargeDayOfWeek: 1, timeOfDay: '17:00',
      country: null as string, timeZoneId: null as string
   };

   // Manual-charge confirmation (owner request 2026-06-12): the backend-previewed period + amount the
   // user is about to charge. Labels are precomputed (display-only; the charge command recomputes).
   manualChargeDialog = {
      visible: false, periodLabel: '', amountLabel: '',
      terminalId: null as string, groupBucketId: null as string
   };

   // Dialog dropdown sources. Weekday ids match System.DayOfWeek (Sun=0..Sat=6).
   chargeIntervals = [{ id: 0, text: 'Daily' }, { id: 1, text: 'Weekly' }];
   weekdays = [
      { id: 0, text: 'Sunday' }, { id: 1, text: 'Monday' }, { id: 2, text: 'Tuesday' },
      { id: 3, text: 'Wednesday' }, { id: 4, text: 'Thursday' }, { id: 5, text: 'Friday' },
      { id: 6, text: 'Saturday' }
   ];

   // Country (grouped) + Timezone as raw labeled selects so every Charge-time row aligns with the rest
   // of the dialog (mirrors the ETS interest-calculator reference, not the bundled timezone-picker).
   // Country drives the timezone list.
   timezoneFamilies = groupTimezoneFamilies();
   timezoneItems: any[] = [];

   confirmTitle = '';

   @ViewChild('confd', { static: false }) confirmationDialog: GenericConfirmationDialogComponent;
   @ViewChild(CollarsBackdateDialogComponent) backdateDialog: CollarsBackdateDialogComponent;
   @ViewChild(CollarsMakeTransactionDialogComponent) makeTransactionDialog: CollarsMakeTransactionDialogComponent;
   @ViewChild(CollarsApplyAfterStateDialogComponent) applyAfterStateDialog: CollarsApplyAfterStateDialogComponent;
   // The acquisition window for the analysis order blocks (CR-20260930-001 M3): the Export window, acquire mode.
   @ViewChild(CollarsAcquireDialogComponent) acquireDialog: CollarsAcquireDialogComponent;

   // Terminal dropdown for Create Portfolio: user's accessible terminals, excluding proxies.
   get availableTerminals(): CollarsTerminalDto[] {
      return this._terminals.filter(t => !t.isProxy)
         .sort((a, b) => compareDisplayNames(a.userName, b.userName));
   }

   etsOnInit(): void {
      this.portfoliosGrid = this.makeBucketGrid(LEVEL_PORTFOLIO);
      this.combosGrid = this.makeBucketGrid(LEVEL_COMBO);
      this.groupsGrid = this.makeBucketGrid(LEVEL_GROUP);
      this.positionsGrid = this.makePositionsGrid();
      this.ledgerGrid = this.makeLedgerGrid();
   }

   etsAfterViewInit(): void {
      // Live updates: position/P&L changes and ledger appends, pushed over ESS (Topic = owning
      // terminal). The frontend keys by the backend DTO class name.
      this._messageBus.of<CollarsPositionChangedDto>('CollarsPositionChangedDto')
         .pipe(takeUntil(this._unsub))
         .subscribe((m: { payload: CollarsPositionChangedDto }) => this.onPositionChanged(m.payload));

      this._messageBus.of<CollarsLedgerChangedDto>('CollarsLedgerChangedDto')
         .pipe(takeUntil(this._unsub))
         .subscribe((m: { payload: CollarsLedgerChangedDto }) => this.onLedgerChanged(m.payload));

      // A session reset settles the synthetic Interest item — the ONE row never pushed as a
      // CollarsPositionChangedDto (positions update live at reset; interest would stay stale,
      // user-reported 2026-06-12). Re-query everything, same treatment as an interest charge.
      this._messageBus.of<CollarsSessionEndedDto>('CollarsSessionEndedDto')
         .pipe(takeUntil(this._unsub))
         .subscribe(() => this.reload());

      // The Portfolio Hedging Analysis session (CR-20260930-001 M2): the lock, the earmarks and the
      // Clear After State item follow the analysis LIVE (M2 smoke: a panel opened before the analysis
      // kept its menus enabled and showed no earmark until reloaded).
      this._messageBus.of<AnalysisSessionChangedDto>(ANALYSIS_SESSION_CHANGED_MESSAGE_TYPE)
         .pipe(takeUntil(this._unsub))
         .subscribe((m: { payload: AnalysisSessionChangedDto }) => this.onAnalysisSessionChanged(m.payload));

      // ESS pushes can be lost while disconnected. This in-app notice follows a successful booking,
      // so Portfolio Items refreshes without waiting for the acquisition dialog to close.
      this._messageBus.of<AnalysisSessionTouchedUIMessage>(ANALYSIS_SESSION_TOUCHED_MESSAGE_TYPE)
         .pipe(takeUntil(this._unsub))
         .subscribe((m: { payload: AnalysisSessionTouchedUIMessage }) => this.onAnalysisSessionTouched(m.payload));

      // ESS is back: pushes sent while the socket was down may never be replayed (a RESTORED session
      // drops its unacked queue), so re-read the analysis lock set once the stream is up.
      this._messageBus.of<EventStreamConnectionChangedUIMessage>('EventStreamConnectionChangedUIMessage')
         .pipe(takeUntil(this._unsub), filter(x => x.payload.isConnected))
         .subscribe(() => this.onEssReconnected());

      // CR-20260930-001 M4 (owner): an analysis overdue card deep-links HERE - the acquisition
      // happens in this panel - with the card's combo auto-selected. The bucket list may still be
      // loading (a panel added by the deep link boots cold), so the selection retries a few times,
      // same road as the analysis panel's own select request.
      this._messageBus.of<OpenSubjectRequest>(COLLARS_SELECT_COMBO_REQUEST_TOPIC)
         .pipe(takeUntil(this._unsub))
         .subscribe(x => {
            const request = x && x.payload;
            // This panel does not answer the panels probe yet, so a request arrives with no target stamp;
            // the gate only refuses a stamp this panel cannot claim.
            if (request && request.panel === 'Collars' && request.comboId && !request.targetPanelId) {
               this.applySelectComboRequest(request, 3);
            }
         });

      // QG history mode: the Symbol column's "(Nd)" DTE counts from the QG cursor day; when that day
      // changes the text is stale for rows whose data object did NOT change — force a cell refresh
      // (the immutableData delta-update path skips unchanged row objects).
      this._qgCursor.dayChanged$
         .pipe(takeUntil(this._unsub))
         .subscribe(() => this.onQgCursorDayChanged());

      this.reload();
   }

   etsOnDestroy(): void {
      this._unsub.next();
      this._unsub.complete();
   }

   // M2: nothing to persist yet (grid column-state persistence lands later).
   protected getState(): any { return null; }

   protected setState(state: any): void { }

   selectTab($event: any): void {
      this.selectedTabIndex = $event.itemIndex;
      this._changeDetector.detectChanges();
      if (this._positionsAutoSizePending) { this.autoSizePortfolioItems(); }
   }

   private autoSizePortfolioItems(): void {
      this._positionsAutoSizePending = true;
      if (!this.isActive || this.selectedTabIndex !== 0 || !this.positionsGrid || !this.positionsGrid.columnApi) { return; }
      // ag-Grid flushes its pending cell-renderer frames before measuring the columns.
      this.positionsGrid.columnApi.autoSizeAllColumns();
      this._positionsAutoSizePending = false;
   }

   private async reload(): Promise<void> {
      this._logger.debug('Collars reload: querying terminals/buckets/positions/ledger');
      try {
         const terminals = await this._shellClient.processQuery<CollarsTerminalDto[]>(new GetCollarsAccessibleTerminals());
         const buckets = await this._shellClient.processQuery<CollarsBucketDto[]>(new ListCollarsBuckets());
         const positions = await this._shellClient.processQuery<CollarsPositionDto[]>(new ListCollarsPositions());
         const ledger = await this._shellClient.processQuery<CollarsLedgerDto[]>(new GetCollarsLedger());
         this._terminals = terminals || [];
         this._buckets = buckets || [];
         this._positions = positions || [];
         this._ledger = ledger || [];
         await this.refreshAnalysisLocks();
         this.applyData();
         this._logger.debug(`Collars reload ok: terminals=${this._terminals.length} buckets=${this._buckets.length} positions=${this._positions.length} ledger=${this._ledger.length}`);
      } catch (e) {
         // Was swallowed (toast only) — log the actual error so a failed load is diagnosable, not just visible.
         this._logger.error('Collars reload failed (one of the terminals/buckets/positions/ledger queries threw)', { error: e.stack || e });
         this._toastr.error('Failed to Load Portfolio Hedging Data');
      }
   }

   private terminalName(terminalId: string): string {
      const t = this._terminals.find(x => x.terminalId === terminalId);
      return t ? t.displayName : terminalId;
   }

   // The seat's current occupant label (live-resolved server-side, already falls back to the terminal
   // name when no user is assigned). Shown wherever Collars used to show the terminal.
   private userName(terminalId: string): string {
      const t = this._terminals.find(x => x.terminalId === terminalId);
      return (t && t.userName) || this.terminalName(terminalId);
   }

   // bucket -> row, decorated with the current client-side P&L roll-up (bucket = sum of its positions).
   // An ARCHIVED bucket reads the archived roll: all its positions are archived (the archive invariant),
   // so its row shows their frozen totals (owner decision 2026-06-10). An active bucket keeps the active
   // roll — archived descendants stay excluded from active parents (only the archived row shows them).
   private toRow(b: CollarsBucketDto): BucketRow {
      const e = (b.isArchived ? this._archivedRoll : this._roll)[b.bucketId];
      return Object.assign({}, b, {
         terminalName: this.terminalName(b.ownerTerminalId),
         userName: this.userName(b.ownerTerminalId),
         sessionPnL: e ? e.session : 0,
         accumulatedPnL: e ? e.accum : 0
      }) as BucketRow;
   }

   // Recompute every bucket's two-horizon total: active positions into _roll (the live view), archived
   // positions into _archivedRoll (frozen — read only by archived bucket rows in toRow). Plain objects
   // keyed by guid string (no Map iteration — ES5 target).
   private rollup(): void {
      const act: { [id: string]: { session: number; accum: number } } = {};
      const arch: { [id: string]: { session: number; accum: number } } = {};
      this._positions.forEach(p => {
         const map = p.isArchived ? arch : act;
         this.addRoll(map, p.portfolioId, p);
         this.addRoll(map, p.comboId, p);
         this.addRoll(map, p.groupId, p);
      });
      this._roll = act;
      this._archivedRoll = arch;
   }

   private addRoll(map: { [id: string]: { session: number; accum: number } }, id: string, p: CollarsPositionDto): void {
      if (!id) { return; }
      const e = map[id] || { session: 0, accum: 0 };
      e.session += p.sessionTotalPnL;
      e.accum += p.accumulatedTotalPnL;
      map[id] = e;
   }

   // Full reload (initial + after a create/delete/record + on a live push): re-roll, repopulate all
   // grids, and re-apply the selection so scope + highlight survive. setRowData/setSelected are
   // programmatic and never fire onRowClicked (user-click only), so they cannot re-enter selection.
   /** Re-read which combos are under analysis and which blocks pend on them. A failed read keeps the last state (the Shell still refuses). */
   private async refreshAnalysisLocks(): Promise<void> {
      try {
         const states = await this._analysisLocks.comboStates();
         const locked = new Set<string>();
         const byCombo: { [comboId: string]: AnalysisComboStateDto } = {};
         const byGroup: { [groupId: string]: AnalysisPendingBlockDto[] } = {};
         states.forEach(c => {
            byCombo[c.comboId] = c;
            if (c.isLocked) { locked.add(c.comboId); }
            c.pendingBlocks.forEach(pb => {
               const groups: { [id: string]: true } = {};
               ((pb.block && pb.block.changes) || []).forEach(ch => { if (ch.groupBucketId) { groups[ch.groupBucketId] = true; } });
               Object.keys(groups).forEach(g => (byGroup[g] = byGroup[g] || []).push(pb));
            });
         });
         this._lockedComboIds = locked;
         this._analysisByCombo = byCombo;
         this._analysisPendingByGroup = byGroup;
      } catch (e) {
         this._logger.warn('Collars analysis-lock read failed; keeping the previous lock set', { error: e.stack || e });
      }
   }

   /** The analysis push (M2): a home assigned = a new portfolio/combo exists → full reload; anything else = lock + earmarks. */
   private async onAnalysisSessionChanged(dto: AnalysisSessionChangedDto): Promise<void> {
      if (!dto) { return; }
      this._logger.debug('Collars analysis push: ' + dto.reason + ' session=' + dto.sessionId + ' combo=' + dto.comboId);
      if (dto.reason === 'home-assigned') {
         await this.reload();
         return;
      }
      await this.refreshAnalysisLocks();
      this.applyData();
      this._changeDetector.detectChanges();
   }

   /** Re-read the pending set as soon as an in-app acquisition succeeds, even if ESS is down. */
   private async onAnalysisSessionTouched(touched: AnalysisSessionTouchedUIMessage): Promise<void> {
      if (!touched || touched.reason !== 'acquired') { return; }
      this._logger.debug('Collars acquired analysis blocks: session=' + touched.sessionId + '; refreshing Portfolio Items');
      await this.refreshAnalysisLocks();
      this.applyData();
      this._changeDetector.detectChanges();
   }

   /** ESS reconnected: the analysis lock set may have changed while the stream was down — re-read it. */
   private async onEssReconnected(): Promise<void> {
      this._logger.debug('Collars analysis lock resync after ESS reconnect');
      await this.refreshAnalysisLocks();
      this.applyData();
      this._changeDetector.detectChanges();
   }

   /** The pending analysis blocks under a bucket (group: its own; combo/portfolio: every descendant group's), deduplicated. */
   private analysisPendingUnder(bucket: CollarsBucketDto): AnalysisPendingBlockDto[] {
      if (!bucket) { return []; }
      const out: AnalysisPendingBlockDto[] = [];
      const seen: { [blockId: string]: true } = {};
      const collect = (b: CollarsBucketDto) => {
         if (b.level === LEVEL_GROUP) {
            (this._analysisPendingByGroup[b.bucketId] || []).forEach(pb => {
               if (!seen[pb.block.blockId]) { seen[pb.block.blockId] = true; out.push(pb); }
            });
            return;
         }
         this._buckets.filter(x => x.level === b.level + 1 && x.parentBucketId === b.bucketId).forEach(collect);
      };
      collect(bucket);
      return out;
   }

   /**
    * "Clear After State" on a bucket with pending analysis blocks (owner 2026-09-30): the SAME operation as
    * the Analysis panel's Revoke, block scope, one call per block. A live session's block goes back to
    * not-exported there (the panel learns it from the push); a dead session's orphan is simply removed.
    * Never touches the old saved-plan mechanism (that has its own Clear Saved State on the portfolio).
    */
   private async clearAnalysisAfterState(bucket: BucketRow): Promise<void> {
      const pending = this.analysisPendingUnder(bucket);
      if (!pending.length) { return; }
      // Short, no detail (owner, M2 smoke): "orphaned" when every block is a dead analysis's, else "pending".
      const allOrphaned = pending.every(p => p.isOrphaned);
      this.confirmTitle = 'Clear After State';
      this._changeDetector.detectChanges();
      try {
         await this.confirmationDialog.show([
            `This will clear ${allOrphaned ? 'orphaned' : ''} pending order blocks from the ${this.levelNoun(bucket.level).toLowerCase()}.`,
            'Do you confirm?'
         ]);
      } catch (e) {
         return;
      }
      let failed = 0;
      for (const pb of pending) {
         try {
            await this._shellClient.processCommand(new RevokeAnalysisBlocks(pb.sessionId, 'block', pb.block.blockId, ''));
            // The in-app notice (CR-20260930-001): the analysis panel beside us re-reads at once,
            // never waiting for the server push an ESS dropout could lose.
            this._messageBus.publish({
               topic: ANALYSIS_SESSION_TOUCHED_MESSAGE_TYPE,
               payload: { sessionId: pb.sessionId, reason: 'revoked' } as AnalysisSessionTouchedUIMessage
            });
         } catch (e) {
            failed++;
            this._logger.error('Collars clear analysis after-state failed: block=' + pb.block.blockId + ' session=' + pb.sessionId, { error: e.stack || e });
         }
      }
      this._logger.info(`Collars clear analysis after-state -> bucket=${bucket.bucketId} cleared=${pending.length - failed} failed=${failed}`);
      if (failed) {
         this._toastr.error(`${failed} of ${pending.length} Block(s) Could Not Be Cleared (See the Log)`);
      } else {
         this._toastr.success(`${pending.length} Pending Order Block(s) Cleared`);
      }
      await this.refreshAnalysisLocks();
      this.applyData();
      this._changeDetector.detectChanges();
   }

   /** Is the bucket (any level) under a combo that is under analysis? A portfolio counts when any child combo is. */
   private isBucketLocked(bucket: CollarsBucketDto): boolean {
      if (!bucket || this._lockedComboIds.size === 0) { return false; }
      const byId: { [id: string]: CollarsBucketDto } = {};
      this._buckets.forEach(b => { byId[b.bucketId] = b; });
      return CollarsAnalysisLockService.combosOf(bucket.bucketId, byId, this._buckets).some(c => this._lockedComboIds.has(c));
   }

   private isPositionLocked(position: CollarsPositionDto): boolean {
      return !!(position && position.comboId && this._lockedComboIds.has(position.comboId));
   }

   private get lockTip(): string {
      return CollarsAnalysisLockService.LOCK_REASON;
   }

   private applyData(): void {
      this.rollup();
      // A selected bucket may have just left the visible set (archived while hidden, or the show-archived
      // toggle flipped off) — clear the stale selection BEFORE the grids and the lower scope repaint.
      this.pruneHiddenSelection();
      // Refresh the after-state indicator snapshot ONCE for this pass before the grids paint their Name
      // cells (the badge renderer reads _afterStateGroupIds, never re-enumerates settings per cell).
      this.refreshAfterStateIndicator();
      this.refreshBucketGrids();
      this.refreshLowerGrids();
   }

   // Archived buckets are hidden unless the user opted in (decision: default hidden, in-memory toggle).
   private isBucketVisible(b: CollarsBucketDto): boolean {
      return this.showArchivedBuckets || !b.isArchived;
   }

   // Drop any selected bucket that is no longer visible; children cascade with their parent (the lower
   // grids re-scope off the surviving selection).
   private pruneHiddenSelection(): void {
      const visible = (id: string): boolean => {
         if (!id) { return true; }   // nothing selected at this level — nothing to prune
         const b = this._buckets.find(x => x.bucketId === id);
         return !!b && this.isBucketVisible(b);
      };
      if (!visible(this._selectedPortfolioId)) {
         this._selectedPortfolioId = null;
         this._selectedComboId = null;
         this._selectedGroupId = null;
      } else if (!visible(this._selectedComboId)) {
         this._selectedComboId = null;
         this._selectedGroupId = null;
      } else if (!visible(this._selectedGroupId)) {
         this._selectedGroupId = null;
      }
   }

   // Recompute the after-state indicator set once per data-load pass. Plain object set (no Map iteration —
   // ES5 target). listAfterStateGroupIds() reads the current user's own settings, so a non-owner session
   // resolves an empty set and every bucket stays uncoloured.
   private refreshAfterStateIndicator(): void {
      const set: { [groupId: string]: true } = {};
      const ids = this._afterStateService.listAfterStateGroupIds();
      ids.forEach(id => { set[id] = true; });
      this._afterStateGroupIds = set;
   }

   private refreshBucketGrids(): void {
      if (this.portfoliosGrid && this.portfoliosGrid.api) {
         // Every bucket subsection lists alphabetically by name (owner, 2026-06-12). Sorted in the data
         // (row order = array order), not via a column sort — no sort arrow, nothing for the user to
         // untoggle. The portfolio grid also orders its Terminal groups deterministically by seat name.
         this.portfoliosGrid.api.setRowData(this._buckets
            .filter(b => b.level === LEVEL_PORTFOLIO && this.isBucketVisible(b))
            .map(b => this.toRow(b))
            .sort((a, b) => (a.terminalName || '').localeCompare(b.terminalName || '')
               || (a.name || '').localeCompare(b.name || '')));
         this.reselect(this.portfoliosGrid, this._selectedPortfolioId);
         // FORCE the Name cells to re-render: with immutableData, setRowData skips cells whose VALUE is
         // unchanged — but the after-state badge is derived from the _afterStateGroupIds snapshot, not the
         // cell value, so an applied/discarded plan would otherwise keep its stale badge painted.
         this.portfoliosGrid.api.refreshCells({ force: true });
      }
      if (this.combosGrid && this.combosGrid.api) {
         this.combosGrid.api.setRowData(this.childRows(LEVEL_COMBO, this._selectedPortfolioId));
         this.reselect(this.combosGrid, this._selectedComboId);
         this.combosGrid.api.refreshCells({ force: true });
      }
      if (this.groupsGrid && this.groupsGrid.api) {
         this.groupsGrid.api.setRowData(this.childRows(LEVEL_GROUP, this._selectedComboId));
         this.reselect(this.groupsGrid, this._selectedGroupId);
         this.groupsGrid.api.refreshCells({ force: true });
      }
   }

   private childRows(level: number, parentId: string): BucketRow[] {
      if (!parentId) {
         return [];
      }
      // Alphabetical by name (owner, 2026-06-12) — feeds the Combo and Group subsections.
      return this._buckets
         .filter(b => b.level === level && b.parentBucketId === parentId && this.isBucketVisible(b))
         .map(b => this.toRow(b))
         .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
   }

   private reselect(grid: GridOptions, bucketId: string): void {
      if (!bucketId || !grid.api) {
         return;
      }
      const node = grid.api.getRowNode(bucketId);
      if (node) {
         node.setSelected(true);
      }
   }

   private setChildRows(grid: GridOptions, level: number, parentId: string): void {
      if (!grid || !grid.api) {
         return;
      }
      grid.api.setRowData(this.childRows(level, parentId));
      this._changeDetector.detectChanges();
   }

   // ---- Portfolio Items + Ledger (scoped to the selected bucket) ----

   private scopedPositions(): CollarsPositionDto[] {
      // Include archived rows now (WB-18): the grid groups them into a separate Archived section
      // (makePositionsGrid). Archived positions keep their bucket ids, so they still scope to the bucket.
      const all = this._positions.slice();
      let scoped: CollarsPositionDto[];
      if (this._selectedGroupId) { scoped = all.filter(p => p.groupId === this._selectedGroupId); }
      else if (this._selectedComboId) { scoped = all.filter(p => p.comboId === this._selectedComboId); }
      else if (this._selectedPortfolioId) { scoped = all.filter(p => p.portfolioId === this._selectedPortfolioId); }
      else { return []; }
      // Active rows first (seeds initial order; the auto group column's sort then keeps Active above
      // Archived across live updates). Stable sort preserves intra-section order.
      return scoped.sort((a, b) => (a.isArchived ? 1 : 0) - (b.isArchived ? 1 : 0));
   }

   private scopedLedger(): CollarsLedgerDto[] {
      if (this._selectedGroupId) { return this._ledger.filter(r => r.groupId === this._selectedGroupId); }
      if (this._selectedComboId) { return this._ledger.filter(r => r.comboId === this._selectedComboId); }
      if (this._selectedPortfolioId) { return this._ledger.filter(r => r.portfolioId === this._selectedPortfolioId); }
      return [];
   }

   private refreshLowerGrids(): void {
      if (this.positionsGrid && this.positionsGrid.api) {
         this.positionsGrid.api.setRowData(this.scopedPortfolioItems());
         this.expandActiveSectionOnAppearance();
         this.expandPendingFillsOnChange();
      }
      if (this.ledgerGrid && this.ledgerGrid.api) {
         this.ledgerGrid.api.setRowData(this.scopedLedger());
      }
      this._changeDetector.detectChanges();
   }

   /** Pending Fills exists only while unfilled analysis orders remain in the selected scope. */
   private scopedPortfolioItems(): PendingFillRow[] {
      if (!this._selectedGroupId && !this._selectedComboId && !this._selectedPortfolioId) { return []; }
      const rows: PendingFillRow[] = this.scopedPositions().map(p => Object.assign({}, p, { isPendingFill: false }) as PendingFillRow);
      const pending = this.scopedPendingFills();
      rows.push(...pending);
      return rows;
   }

   /** Each pending change is a separate row; its signed quantity is kept apart from the live position. */
   private scopedPendingFills(): PendingFillRow[] {
      const scopeId = this._selectedGroupId || this._selectedComboId || this._selectedPortfolioId;
      const scope = this._buckets.find(b => b.bucketId === scopeId);
      if (!scope) { return []; }

      const rows: PendingFillRow[] = [];
      this.analysisPendingUnder(scope).forEach(pb => {
         (pb.block.changes || []).forEach(change => {
            const group = this._buckets.find(b => b.bucketId === change.groupBucketId && b.level === LEVEL_GROUP);
            if (!group) { return; }
            const combo = this._buckets.find(b => b.bucketId === group.parentBucketId);
            const portfolio = combo && this._buckets.find(b => b.bucketId === combo.parentBucketId);
            rows.push({
               positionId: 'pending-fill:' + pb.sessionId + ':' + change.changeId,
               terminalId: group.ownerTerminalId,
               tradingInstrumentCode: change.tradingInstrumentCode,
               kind: change.kind,
               portfolioId: portfolio ? portfolio.bucketId : null,
               comboId: combo ? combo.bucketId : null,
               groupId: group.bucketId,
               netPosition: change.side * change.qty,
               avgPx: 0,
               openPx: null,
               lastMatchPx: 0,
               marketValue: 0,
               sessionRealizedPnL: 0,
               sessionUnrealizedPnL: 0,
               settledPnL: 0,
               sessionTotalPnL: 0,
               accumulatedTotalPnL: 0,
               isArchived: false,
               archivedDate: null,
               isFlat: false,
               isInterest: false,
               isPendingFill: true,
               analysisBlockId: pb.block.blockId
            } as PendingFillRow);
         });
      });
      return rows;
   }

   private expandPendingFillsOnChange(): void {
      const api = this.positionsGrid.api;
      let pending: any = null;
      api.forEachNode((n: any) => { if (n.group && n.key === 'Pending Fills') { pending = n; } });
      const hasRows = !!(pending && pending.allLeafChildren && pending.allLeafChildren.some((n: any) => n.data && n.data.isPendingFill));
      if (pending && hasRows && !this._pendingFillsWerePresent && !pending.expanded) { pending.setExpanded(true); }
      if (pending && !hasRows && this._pendingFillsWerePresent && pending.expanded) { pending.setExpanded(false); }
      this._pendingFillsWerePresent = hasRows;
   }

   // isGroupOpenByDefault seeds a section only at its FIRST render, which does not reliably cover a
   // section group node created later by an immutableData delta-update. Expand explicitly on every
   // absent -> present transition of "Active Positions"; no re-force while it stays present, so a
   // user's collapse survives live refreshes.
   private expandActiveSectionOnAppearance(): void {
      const api = this.positionsGrid.api;
      let active: any = null;
      api.forEachNode((n: any) => { if (n.group && n.key === 'Active Positions') { active = n; } });
      if (active && !this._activeSectionWasPresent && !active.expanded) {
         active.setExpanded(true);
      }
      this._activeSectionWasPresent = !!active;
   }

   private onPositionChanged(p: CollarsPositionDto): void {
      if (!p) { return; }
      // Verbose trace disabled — fired per pushed position update (per quote tick per open position).
      // this._logger.debug(`Collars push position: ${p.tradingInstrumentCode || ''} net=${p.netPosition} sessionPnL=${p.sessionTotalPnL} accum=${p.accumulatedTotalPnL}${p.isRemoved ? ' [removed]' : ''}`);
      const i = this._positions.findIndex(x => x.positionId === p.positionId);
      if (p.isRemoved) {
         // A With-P&L transfer merge absorbed this position — drop the row (don't resurrect it).
         if (i >= 0) { this._positions.splice(i, 1); }
      } else if (i >= 0) {
         this._positions[i] = p;
      } else {
         this._positions.push(p);
      }
      this.applyData(); // re-roll buckets + refresh the lower grids
   }

   private onLedgerChanged(r: CollarsLedgerDto): void {
      if (!r) { return; }
      this._logger.debug(`Collars push ledger: type=${r.transactionType} ${r.tradingInstrumentCode || ''} qty=${r.quantity} net=${r.netPosition} itemPnL=${r.itemPnL}`);
      if (!this._ledger.some(x => x.recordId === r.recordId)) {
         this._ledger.push(r);
      }
      // An Interest charge posted a ledger row + bumped the Interest item's accumulated total (which the
      // backend derives from the ledger). Reload so the synthetic Interest item reflects the new total.
      if (r.transactionType === TXN_INTEREST) {
         this.reload();
         return;
      }
      this.refreshLowerGrids();
   }

   // The QG cursor crossed a day boundary — every option row's "(Nd)" suffix changed without any row
   // data changing, so the immutableData delta path won't touch the cells. Force-refresh: the Symbol
   // valueGetter re-runs and re-reads the cursor day. Rare (once per emulated day) — force is cheap here.
   private onQgCursorDayChanged(): void {
      if (this.positionsGrid && this.positionsGrid.api) {
         this.positionsGrid.api.refreshCells({ force: true });
      }
   }

   private num(v: number): string {
      return (v === null || v === undefined) ? '' : (+v).toFixed(2);
   }

   // Price cells carry a dollar sign (owner, 2026-06-12); sign rides OUTSIDE the symbol ("-$1.25").
   private px(v: number): string {
      if (v === null || v === undefined) { return ''; }
      const n = +v;
      return (n < 0 ? '-$' : '$') + Math.abs(n).toFixed(2);
   }

   // Whole-dollar money for the Settle-at-Expiry P&L breakdown (owner 2026-06-15: a P&L figure is always whole
   // given the ×100 contract multiplier — show NO cents; a zero term reads "$0", not "$0.00"). Negatives read
   // "-$..."; positives carry NO leading + (the breakdown's own ± operator already carries the premium's sign).
   private usdWhole(v: number): string {
      if (v === null || v === undefined) { return ''; }
      const n = Math.round(+v);
      if (n === 0) { return '$0'; }
      return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
   }

   // Plain currency for the Moneyness intrinsic (owner 2026-06-15: show a $): "$5.00" / "-$1,234.56",
   // grouped, NO forced + sign (intrinsic is always >= 0). Template-bound -> public.
   usd(v: number): string {
      if (v === null || v === undefined) { return ''; }
      const n = +v;
      return (n < 0 ? '-$' : '$') + Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
   }

   // Settle-at-Expiry "P&L" row: the explicit breakdown "$mrkt (mrkt.) ± $prem (prem.) = $total" (owner
   // 2026-06-15). mrkt = the settlement OPERATION P&L alone; prem = premium paid (−) or received (+); total =
   // net realized P&L (no leading + — a loss reads "-$..."). The operator carries the premium's sign; a zero
   // market/premium term reads "$0". e.g. "$38,172 (mrkt.) - $7,000 (prem.) = $31,172".
   settlePnlBreakdown(): string {
      const d = this.settleDialog;
      // Market (operation) P&L = total − premium, derived HERE so the row needs only realizedPnL + premiumAmount
      // (both already on the wire) — no dependency on a backend field that may post-date the running shell build.
      const market = (d.realizedPnL || 0) - (d.premiumAmount || 0);
      const op = d.premiumAmount >= 0 ? '+' : '-';
      return `${this.usdWhole(market)} (mrkt.) ${op} ${this.usdWhole(Math.abs(d.premiumAmount))} (prem.) = ${this.usdWhole(d.realizedPnL)}`;
   }

   // Settle-at-Expiry "Position" row: the asset change the settlement spawns (physical exercise/assignment).
   // "+200 SPY" when you acquire (buy), "-200 SPY" when you deliver (sell); "N/A" when nothing spawns
   // (cash-settled or worthless). Underlying is a stock -> dispName returns its friendly name (e.g. "Apple").
   settlePositionChange(): string {
      const d = this.settleDialog;
      if (!d.spawnsUnderlying) { return 'N/A'; }
      const sign = d.underlyingSide === 'Buy' ? '+' : '-';
      return `${sign}${d.underlyingQty} ${this.dispName(d.underlyingCode, false)}`;
   }

   // Interest charge metadata (owner spec 2026-06-12). Rate fraction -> trimmed percent ("0.1" -> "10%",
   // "0.045" -> "4.5%"); day count -> "Nd" ("7d"). Both BLANK on 0/null: a zero here is the legacy
   // (pre-stamp) sentinel — rendered blank, never backfilled.
   private pct(v: number): string {
      return v ? `${parseFloat((+v * 100).toFixed(2))}%` : '';
   }

   private days(v: number): string {
      return v ? `${v}d` : '';
   }

   // Reference pattern (hedge-matrix hg-hedge-matrix.component.ts:1900): an option code resolves via
   // getOptionDisplayName (parseOptionTicker -> makeOptionTickerDisplayName); a stock/futures code via
   // getDisplayNameForTicker (registered-instrument map). The two are distinct resolvers — an option
   // code is NOT a registered instrument, so getDisplayNameForTicker returns it raw. Discriminate by
   // parse validity (an option code has >=5 space-separated tokens), not by the '@' prefix (option
   // expiration roots also use '@'). Portfolio Items appends the live DTE "(Nd)"; the ledger omits it.
   private dispName(code: string, withDte: boolean): string {
      if (!code) { return ''; }
      const t = parseOptionTicker(code);
      if (t) {
         // Option: Collars name with weekday-bearing date. Resolve the underlying's friendly name via
         // the service (as getOptionDisplayName does internally), then compose with the Collars helper.
         // Portfolio Items appends the live DTE "(Nd)"; the ledger omits it.
         const ulName = this._displayNames.getDisplayNameForTicker(t.underlying);
         const name = makeCollarsOptionDisplayName(t, ulName);
         // DTE counts from the QG cursor day in a QG-history shell (undefined elsewhere -> the
         // daysToExpiration wall-clock fallback). Keyed by the UNDERLYING — the cursor is per-stream.
         return withDte ? `${name} (${daysToExpiration(t.expiration, this._qgCursor.cursorDayFor(t.underlying))}d)` : name;
      }
      return this._displayNames.getDisplayNameForTicker(code) || code;
   }

   private txnLabel(v: number): string {
      return (v >= 0 && v < COLLARS_TXN_LABELS.length) ? COLLARS_TXN_LABELS[v] : String(v);
   }

   private moneyCol(header: string, field: string): ColDef {
      return Object.assign({ headerName: header, field, maxWidth: 130, aggFunc: 'sum' }, defaultMoneyCellDefinition) as ColDef;
   }

   private isPendingFillRowOrGroup(params: any): boolean {
      return !!(params && ((params.data && params.data.isPendingFill) ||
         (params.node && params.node.group && params.node.key === 'Pending Fills')));
   }

   // Market Value (owner visual-request 2026-10-01): same money cells as the P&L columns (group rows
   // sum their leaves via aggFunc). Leaf rows blank when the backend sends 0 — Interest (no quote)
   // and flat (no open net) rows; a group row (no data) always formats its aggregated sum.
   private marketValueCol(): ColDef {
      return Object.assign(
         { headerName: 'Market Value', field: 'marketValue', maxWidth: 130, aggFunc: 'sum' },
         defaultMoneyCellDefinition,
         {
            valueFormatter: (p: any) => (p.data && (p.data.isInterest || !p.value))
               ? ''
               : defaultMoneyCellDefinition.valueFormatter(p)
         }) as ColDef;
   }

   private makeBucketGrid(level: number): GridOptions {
      const self = this;
      const grouped = level === LEVEL_PORTFOLIO;
      // centeredColumnDef (defaultColDef below) centers the HEADER only; give the P&L columns a cell
      // text-align so their bodies center too (cosmetics, 2026-06-12). Name stays left-aligned.
      const centerCell = { cellStyle: { 'text-align': 'center' } };

      // After-state indicator: a solid #41FC41 rectangle BEFORE the bucket name when this bucket (or a
      // descendant group) has a saved after-state key (owner restyle, vrequest follow-up 2026-06-10 —
      // replaced the earlier glow dot). A sign, NOT a colour on the name text/background — green text
      // reads as P&L. Owner-session only — listAfterStateGroupIds() reads the current user's own
      // settings, so a non-owner session resolves an empty set and shows no badge. Plain-string renderer
      // (ag-Grid eval), so the name is HTML-escaped.
      const afterStateNameRenderer = (p: any): string => {
         const name = escapeHtml(p && p.value != null ? p.value : '');
         if (p && p.data && self.bucketHasAfterState(p.data as BucketRow)) {
            return '<span class="ets-after-state-badge" title="Has a saved After State"></span>' + name;
         }
         // The same sign for a pending order block of the Portfolio Hedging Analysis (M2): "has an
         // unapplied after-state", whichever mechanism produced it (owner: parity of the earmark).
         if (p && p.data && self.bucketHasAnalysisPending(p.data as BucketRow)) {
            return '<span class="ets-after-state-badge" title="Has pending orders from the Portfolio Hedging Analysis (Apply After State / Clear After State)"></span>' + name;
         }
         return name;
      };

      const columnDefs: ColDef[] = [];
      if (grouped) {
         columnDefs.push({ headerName: 'Terminal', field: 'terminalName', rowGroup: true, hide: true } as ColDef);
      } else {
         columnDefs.push({ headerName: 'Name', field: 'name', flex: 1, cellRenderer: afterStateNameRenderer } as ColDef);
      }
      columnDefs.push(Object.assign(self.moneyCol('Sess.P&L', 'sessionPnL'), centerCell));
      columnDefs.push(Object.assign(self.moneyCol('Acc.P&L', 'accumulatedPnL'), centerCell));

      const options: GridOptions = {
         defaultColDef: centeredColumnDef,
         columnDefs,
         rowSelection: 'single',
         suppressCellSelection: true,
         suppressAggFuncInHeader: true,
         immutableData: true,   // delta-update by getRowNodeId so live P&L pushes keep selection (no blink)
         getRowNodeId: (data: BucketRow) => data.bucketId,
         // Archived buckets read YELLOW (owner decision 2026-06-10 — the same signal as the Archived
         // Positions section label). rowClassRules, not getRowStyle: class rules re-evaluate when row
         // data changes, so a just-archived row recolors on the reload's delta-update without a redraw.
         // Money cells keep their red/green P&L classes (those cell rules win inside the cells).
         rowClassRules: {
            'ets-archived-bucket-row': (params: any) => !!(params.data && params.data.isArchived)
         },
         onGridReady: () => self.applyData(),
         onRowClicked: (e: any) => self.onRowClicked(level, e),
         getContextMenuItems: (params: any) => self.contextMenu(level, params)
      };

      if (grouped) {
         options.groupDefaultExpanded = -1;
         options.groupUseEntireRow = false;
         // Sort by the displayed occupant name, keeping the terminal key and child order intact.
         options.defaultGroupSortComparator = (nodeA, nodeB) => {
            const displayedName = (node: any): string => {
               const leaf = node.allLeafChildren && node.allLeafChildren[0];
               return (leaf && leaf.data && leaf.data.userName) || node.key || '';
            };
            return compareDisplayNames(displayedName(nodeA), displayedName(nodeB));
         };
         options.autoGroupColumnDef = {
            headerName: 'Name',
            minWidth: 180,
            flex: 1,
            cellRendererParams: {
               suppressCount: true,
               // Group rows: agGroupCellRenderer shows the group KEY (terminalName) and ignores valueGetter
               // for the label, so override the displayed text via innerRenderer -> the seat's current user
               // (read off a representative leaf row; same pattern as the positions grid below). Leaf
               // (Portfolio) rows append the after-state badge when they cascade a saved after-state;
               // group-header (Terminal) rows have no data and never show the badge.
               innerRenderer: (params: any) => {
                  if (params.node && params.node.group) {
                     const leaf = params.node.allLeafChildren && params.node.allLeafChildren[0];
                     return escapeHtml((leaf && leaf.data && leaf.data.userName) || params.value || '');
                  }
                  return afterStateNameRenderer(params);   // leaf: portfolio name (+ badge when cascading)
               }
            },
            valueGetter: (p: any) => (p.node && p.node.group) ? p.node.key : (p.data ? p.data.name : '')
         };
      }

      return options;
   }

   // Resolve a bucket id to its name from the loaded bucket tree (the position DTO carries only ids;
   // names live on the buckets, same lookup the bucket grids use).
   private bucketName(bucketId: string): string {
      if (!bucketId) { return ''; }
      const b = this._buckets.find(x => x.bucketId === bucketId);
      return b ? b.name : '';
   }

   // Type column: Asset for an asset leg; for an option, the contract side parsed from its code.
   private instrumentType(data: CollarsPositionDto): string {
      if (!data) { return ''; }
      if (data.kind === KIND_ASSET) { return 'Asset'; }
      const t = parseOptionTicker(data.tradingInstrumentCode);
      return t ? t.type : 'Option';   // 'Call' | 'Put'
   }

   private makePositionsGrid(): GridOptions {
      const self = this;
      const centeredCells = Object.assign({}, centeredColumnDef, { cellStyle: { 'text-align': 'center' } });
      return {
         defaultColDef: centeredCells,
         columnDefs: [
            // WB-18: group rows into an Active section and a (collapsed) Archived section. Hidden group-key
            // column — full-width group rows render as the section headers (groupUseEntireRow below).
            // Hidden group key — drives the Active/Archived sections. The Symbol auto group column (below)
            // renders it, so the expander is ingrained in Symbol (ETS-CORE pattern); no separate group column.
            { headerName: 'Status', colId: 'archiveStatus', rowGroup: true, hide: true,
              valueGetter: (p: any) => {
                 if (!p.data) { return ''; }
                 if (p.data.isPendingFill) { return 'Pending Fills'; }
                 return p.data.isArchived ? 'Archived Positions' : 'Active Positions';
              } } as ColDef,
            { headerName: 'Type', colId: 'type', maxWidth: 90, valueGetter: (p: any) => !p.data ? '' : (p.data.isInterest ? 'Interest' : p.data.kind === KIND_ASSET ? 'Stock' : self.instrumentType(p.data)) } as ColDef,
            // For a flat/archived position (no floating) Session P&L == realized and Accumulated ==
            // accumulated-realized, so these two existing two-horizon columns ARE the archived "Realized/
            // Accumulated" (FR-M3-7) — no separate Realized column, which FR-M2-7 forbids.
            Object.assign(self.moneyCol('Session P&L', 'sessionTotalPnL'), {
               valueFormatter: (p: any) => self.isPendingFillRowOrGroup(p) ? '' : defaultMoneyCellDefinition.valueFormatter(p)
            }),
            Object.assign(self.moneyCol('Accumulated P&L', 'accumulatedTotalPnL'), {
               valueFormatter: (p: any) => self.isPendingFillRowOrGroup(p) ? '' : defaultMoneyCellDefinition.valueFormatter(p)
            }),
            // The synthetic Interest item carries charge metadata here (owner spec 2026-06-12):
            // Net Position = lifetime charged DAYS ("14d"), Avg. Px = the CURRENT configured rate ("10%").
            // No live quote / open px (WB-17). Zero day count / rate renders blank (legacy, no backfill).
            { headerName: 'Net Position', field: 'netPosition', maxWidth: 110,
              valueFormatter: (p: any) => !p.data || p.data.isPendingFillPlaceholder ? '' : (p.data.isInterest ? self.days(p.value) : ((p.value === null || p.value === undefined) ? '' : (p.data.isPendingFill && p.value > 0 ? '+' : '') + String(p.value))),
              // Pending Buy/Long uses the Portfolio Hedging blue; Sell/Short uses its red.
              cellStyle: (p: any) => {
                 const style: any = { 'text-align': 'center' };
                 if (p.data && p.data.isPendingFill) { style.color = p.data.netPosition > 0 ? 'cornflowerblue' : 'red'; }
                 return style;
              } } as ColDef,
            { headerName: 'Avg. Px', field: 'avgPx', maxWidth: 100, valueFormatter: (p: any) => (p.data && (p.data.isInterest || p.data.isPendingFill || p.data.isPendingFillPlaceholder)) ? (p.data.isInterest ? self.pct(p.value) : '') : self.px(p.value) } as ColDef,
            { headerName: 'Last Match Px.', field: 'lastMatchPx', maxWidth: 110, valueFormatter: (p: any) => (p.data && (p.data.isInterest || p.data.isPendingFill || p.data.isPendingFillPlaceholder)) ? '' : self.px(p.value) } as ColDef,
            { headerName: 'Open Px.', field: 'openPx', maxWidth: 100, valueFormatter: (p: any) => (p.data && p.data.isInterest) ? '' : self.px(p.value) } as ColDef,
            // Market Value (owner visual-request 2026-10-01): signed current market value, computed
            // backend-side (open net × current price × engine multiplier) on the position DTOs.
            Object.assign(self.marketValueCol(), {
               valueFormatter: (p: any) => self.isPendingFillRowOrGroup(p) ? '' : (p.data && (p.data.isInterest || !p.value)
                  ? ''
                  : defaultMoneyCellDefinition.valueFormatter(p))
            }),
            // Bucket columns: Combo, Group, Portfolio (owner re-order 2026-06-12).
            { headerName: 'Combo', colId: 'combo', minWidth: 120, valueGetter: (p: any) => p.data ? self.bucketName(p.data.comboId) : '' } as ColDef,
            { headerName: 'Group', colId: 'group', minWidth: 120, valueGetter: (p: any) => p.data ? self.bucketName(p.data.groupId) : '' } as ColDef,
            { headerName: 'Portfolio', colId: 'portfolio', minWidth: 120, valueGetter: (p: any) => p.data ? self.bucketName(p.data.portfolioId) : '' } as ColDef,
            { headerName: 'User', colId: 'user', minWidth: 120, valueGetter: (p: any) => p.data ? self.userName(p.data.terminalId) : '', tooltipValueGetter: (p: any) => p.data ? self.terminalName(p.data.terminalId) : '' } as ColDef,
            // Archived Date (WB-18, FR-M3-7): app-zone timestamp on archived rows; blank while active.
            // LAST column (owner, 2026-06-10) — terminal metadata trails the live numbers.
            { headerName: 'Archived Date', colId: 'archivedDate', minWidth: 150,
              valueFormatter: (p: any) => (p.data && p.data.archivedDate) ? makeCollarsLedgerTimestamp(p.data.archivedDate, self._timestamps.getDefaultTimezone('UTC')) : '' } as ColDef
         ],
         rowSelection: 'single',
         suppressCellSelection: true,
         suppressAggFuncInHeader: true,
         // Autosizing measures rendered cells; include columns outside the horizontal viewport.
         suppressColumnVirtualisation: true,
         // Two sections via an auto group column (same pattern as the bucket grids + ETS-CORE Portfolio
         // Items): Active expanded, Archived collapsed by default. isGroupOpenByDefault seeds only a
         // group's FIRST render, so a user's later expand/collapse survives live delta-updates. The
         // section label is colored like ETS-CORE (portfolio-items-archived-group-row.ts): Archived =
         // yellow, Active = #e8e2e2.
         // The auto group column IS the Symbol column (ETS-CORE pattern, portfolio-items-grid-options.ts
         // getAutoGroupColumnDefForSymbolColumn): the expander is ingrained in Symbol — group rows render
         // the colored section label, leaf rows render the instrument display name. One combined column.
         autoGroupColumnDef: {
            headerName: 'Symbol',
            field: 'tradingInstrumentCode',
            // The ETS-CORE symbol-column width +12% (owner, 2026-06-12): the Items name carries the
            // live "(Nd)" DTE suffix the ledger omits, so the shared constant clips long option names.
            minWidth: Math.round(AG_SYMBOL_COL_MIN_WIDTH * 1.12),
            flex: 1,
            // Section order is explicit: Pending Fills, Active, Archived. A column sort re-applies on every refresh (incl. live
            // immutableData delta-updates — row order alone does NOT, since existing group nodes keep their
            // place). The sort ARROW is hidden via SCSS (col-id "ag-Grid-AutoColumn") to match ETS-CORE.
            sort: 'asc',
            comparator: (a: any, b: any, nodeA: any, nodeB: any) => {
               const rank = (n: any) => !n ? 0 : (n.key === 'Pending Fills' ? 0 : (n.key === 'Active Positions' ? 1 : 2));
               return rank(nodeA) - rank(nodeB);
            },
            cellRendererParams: {
               suppressCount: true,
               innerRenderer: (params: any) => {
                  if (params.node && params.node.group) {
                     // Archived keeps its established yellow label; other section labels share the neutral tone.
                     const color = params.value === 'Archived Positions' ? 'yellow' : '#e8e2e2';
                     const badge = params.value === 'Pending Fills'
                        ? '<span class="ets-after-state-badge" title="Has pending fills"></span>'
                        : '';
                     return badge + '<span style="color:' + color + '">' + params.value + '</span>';
                  }
                  return params.value == null ? '' : params.value;   // leaf: instrument display name
               }
            },
            valueGetter: (params: any) => {
               if (!params.node || params.node.group || params.node.footer || !params.data) { return null; }
               return params.data.isInterest ? '%' : self.dispName(params.data.tradingInstrumentCode, true);
            }
         },
         isGroupOpenByDefault: (params: any) => params.key === 'Active Positions',
         immutableData: true,   // delta-update by positionId so per-quote refresh keeps selection (no loss)
         getRowNodeId: (data: PendingFillRow) => data.positionId,
         getContextMenuItems: (params: any) => self.positionContextMenu(params),
         onRowDoubleClicked: (e: any) => self.onPositionRowDoubleClicked(e),
         onGridReady: () => self.refreshLowerGrids(),
         // Pending orders can be the first data. Size here and again after actual fills are applied.
         onFirstDataRendered: () => self.autoSizePortfolioItems(),
         onGridSizeChanged: args => {
            if (self._positionsAutoSizePending && args.clientWidth > 0 && args.clientHeight > 0) {
               self.autoSizePortfolioItems();
            }
         }
         // isGroupOpenByDefault is a real ag-Grid (Enterprise) option not in the community GridOptions type.
      } as GridOptions;
   }

   // The same-item tape feeding a master row's detail grid: that position's transactions (Open..Reverse)
   // up to and including the master's sequence, ascending. Single source for both the detail rows and
   // the computed detail-row height.
   private detailTapeFor(master: CollarsLedgerDto): CollarsLedgerDto[] {
      if (!master) { return []; }
      // Fund rule: every ledger row is expandable and the ledger does NOT differentiate by transaction
      // type. The detail is that position's full history in the bucket up to (and including) the expanded
      // row, ascending — origination → this row — regardless of trigger (trade / transfer / interest).
      // Interest rows share positionId = the Interest group's bucket id, so an Interest row's detail is
      // that bucket's interest history. A lone receiving row self-duplicates (shows itself) — acceptable.
      return this._ledger
         .filter(r => r.positionId === master.positionId && r.sequence <= master.sequence)
         .sort((a, b) => a.sequence - b.sequence);
   }

   private makeLedgerGrid(): GridOptions {
      const self = this;
      // centeredColumnDef centers the HEADER (centeredHeader template); add a cell text-align so the
      // cell bodies center too (spec: all cells + headers centered).
      const centeredCells = Object.assign({}, centeredColumnDef, { cellStyle: { 'text-align': 'center' } });
      return {
         defaultColDef: centeredCells,
         columnDefs: [
            // agGroupCellRenderer puts the drill-down arrow on the Symbol column (master rows only).
            { headerName: 'Symbol', field: 'tradingInstrumentCode', flex: 1, minWidth: AG_SYMBOL_COL_MIN_WIDTH, cellRenderer: 'agGroupCellRenderer', valueFormatter: (p: any) => self.dispName(p.value, false) } as ColDef,
            { headerName: 'Trans. Date', field: 'transactionDate', minWidth: 215, valueFormatter: (p: any) => makeCollarsLedgerTimestamp(p.value, self._timestamps.getDefaultTimezone('UTC')) } as ColDef,
            // The Note (provenance: "Interest charged (weekly)", fill notes, ...) rides as a tooltip on
            // Trans. Type (owner, 2026-06-12) — any row that carries one, not just Interest.
            { headerName: 'Trans. Type', field: 'transactionType', maxWidth: 120, valueFormatter: (p: any) => self.txnLabel(p.value), tooltipValueGetter: (p: any) => (p.data && p.data.note) || '' } as ColDef,
            // Price wears the dollar sign; an Interest row carries charge metadata instead (owner spec
            // 2026-06-12): Price = stamped rate ("10%"), Qty = charge days ("7d"), Net Position = running
            // charged days ("14d"). Zeros = a legacy (pre-stamp) row -> blank, never backfilled.
            { headerName: 'Price', field: 'price', maxWidth: 90, valueFormatter: (p: any) => (p.data && p.data.transactionType === TXN_INTEREST) ? self.pct(p.value) : self.px(p.value) } as ColDef,
            { headerName: 'Qty', field: 'quantity', maxWidth: 80, valueFormatter: (p: any) => (p.data && p.data.transactionType === TXN_INTEREST) ? self.days(p.value) : ((p.value === null || p.value === undefined) ? '' : String(p.value)) } as ColDef,
            { headerName: 'Net Position', field: 'netPosition', maxWidth: 110, valueFormatter: (p: any) => (p.data && p.data.transactionType === TXN_INTEREST) ? self.days(p.value) : ((p.value === null || p.value === undefined) ? '' : String(p.value)) } as ColDef,
            // P&L block rides right after Net Position, narrowest scope first (owner, 2026-06-12).
            self.moneyCol('Group P&L', 'groupPnL'),
            self.moneyCol('Combo P&L', 'comboPnL'),
            self.moneyCol('P&L', 'itemPnL'),
            self.moneyCol('Portfolio P&L', 'portfolioPnL'),
            // Bucket metadata: Combo, Group, Portfolio, User (owner re-order 2026-06-12).
            { headerName: 'Combo', field: 'comboName', minWidth: 120 } as ColDef,
            { headerName: 'Group', field: 'groupName', minWidth: 120 } as ColDef,
            { headerName: 'Portfolio', field: 'portfolioName', minWidth: 120 } as ColDef,
            { headerName: 'User', colId: 'user', minWidth: 120, valueGetter: (p: any) => p.data ? self.userName(p.data.terminalId) : '', tooltipValueGetter: (p: any) => p.data ? self.terminalName(p.data.terminalId) : '' } as ColDef
         ],
         rowSelection: 'single',
         suppressCellSelection: true,
         suppressAggFuncInHeader: true,
         immutableData: true,   // delta-update by recordId (consistency with the other grids)
         getRowNodeId: (data: CollarsLedgerDto) => data.recordId,
         // Column sizing pair (owner, 2026-06-12) + the defaults the implicit menu used to provide.
         getContextMenuItems: (params: any) => [
            'autoSizeAll',
            { name: 'Size Columns To Fit', action: () => params.api.sizeColumnsToFit() },
            'separator', 'copy', 'export'
         ],
         // Override rows are highlighted orange (ETS-CORE parity — a manual re-base stands out from trades).
         getRowStyle: (args: any) => {
            const d = args && args.data;
            return (d && d.transactionType === TXN_OVERRIDE) ? { background: 'orange', color: 'black' } : null;
         },
         masterDetail: true,
         // detailRowAutoHeight doesn't reserve the detail grid's horizontal-scrollbar height (our 9
         // detail cols overflow), so it clipped the last row. Size the detail row explicitly from the
         // actual theme sizes + a cushion for the scrollbar and a little padding.
         getDetailRowHeight: (params: any) => {
            const count = Math.max(self.detailTapeFor(params.data as CollarsLedgerDto).length, 1);
            let rowH = 28, headerH = 32;
            const sizes = params.api && params.api.getSizesForCurrentTheme && params.api.getSizesForCurrentTheme();
            if (sizes) { rowH = sizes.rowHeight || rowH; headerH = sizes.headerHeight || headerH; }
            // header + the tape rows + two spare row-heights of breathing room (owner spec); the spare
            // rows also clear the detail grid's horizontal scrollbar.
            return headerH + (count + 2) * rowH;
         },
         // Fund rule: EVERY ledger row is expandable (no differentiation by transaction type).
         isRowMaster: (data: any) => !!data,
         detailCellRendererParams: {
            detailGridOptions: {
               defaultColDef: centeredCells,
               columnDefs: [
                  { headerName: 'Symbol', field: 'tradingInstrumentCode', flex: 1, minWidth: 170, valueFormatter: (p: any) => self.dispName(p.value, false) } as ColDef,
                  { headerName: 'Trans. Date', field: 'transactionDate', minWidth: 215, valueFormatter: (p: any) => makeCollarsLedgerTimestamp(p.value, self._timestamps.getDefaultTimezone('UTC')) } as ColDef,
                  { headerName: 'Trans. Type', field: 'transactionType', maxWidth: 120, valueFormatter: (p: any) => self.txnLabel(p.value), tooltipValueGetter: (p: any) => (p.data && p.data.note) || '' } as ColDef,
                  // Interest charge metadata, same treatment as the master grid (owner spec 2026-06-12).
                  { headerName: 'Price', field: 'price', maxWidth: 90, valueFormatter: (p: any) => (p.data && p.data.transactionType === TXN_INTEREST) ? self.pct(p.value) : self.px(p.value) } as ColDef,
                  { headerName: 'Qty', field: 'quantity', maxWidth: 80, valueFormatter: (p: any) => (p.data && p.data.transactionType === TXN_INTEREST) ? self.days(p.value) : ((p.value === null || p.value === undefined) ? '' : String(p.value)) } as ColDef,
                  { headerName: 'Net Position', field: 'netPosition', maxWidth: 110, valueFormatter: (p: any) => (p.data && p.data.transactionType === TXN_INTEREST) ? self.days(p.value) : ((p.value === null || p.value === undefined) ? '' : String(p.value)) } as ColDef,
                  // Same P&L order as the master ledger grid (owner, 2026-06-12).
                  self.moneyCol('Group P&L', 'groupPnL'),
                  self.moneyCol('Combo P&L', 'comboPnL'),
                  self.moneyCol('P&L', 'itemPnL'),
                  self.moneyCol('Portfolio P&L', 'portfolioPnL')
               ],
               // Column sizing pair (owner, 2026-06-12) — params.api here is the DETAIL grid's api.
               getContextMenuItems: (params: any) => [
                  'autoSizeAll',
                  { name: 'Size Columns To Fit', action: () => params.api.sizeColumnsToFit() },
                  'separator', 'copy', 'export'
               ]
            },
            // Same-item tape up to and including the master row (client-side: the ledger is fully
            // loaded). Each row carries its own frozen bucket-P&L snapshots.
            getDetailRowData: (params: any) => {
               params.successCallback(self.detailTapeFor(params.data as CollarsLedgerDto));
            }
         },
         onGridReady: () => self.refreshLowerGrids(),
         // Size every column to its rendered content once the first rows paint (owner, 2026-06-12).
         onFirstDataRendered: (args: FirstDataRenderedEvent) => args.columnApi.autoSizeAllColumns()
         // getDetailRowHeight is a real ag-Grid (Enterprise master/detail) option not in the community GridOptions type.
      } as GridOptions;
   }

   private onRowClicked(level: number, event: any): void {
      if (event && event.node && event.node.group) {
         return; // terminal group-header row
      }
      const row = (event && event.data) ? event.data as BucketRow : null;
      const id = row ? row.bucketId : null;

      if (level === LEVEL_PORTFOLIO) {
         this._selectedPortfolioId = id;
         this._selectedComboId = null;
         this._selectedGroupId = null;
         this.setChildRows(this.combosGrid, LEVEL_COMBO, id);
         this.setChildRows(this.groupsGrid, LEVEL_GROUP, null);
      } else if (level === LEVEL_COMBO) {
         this._selectedComboId = id;
         this._selectedGroupId = null;
         this.setChildRows(this.groupsGrid, LEVEL_GROUP, id);
      } else if (level === LEVEL_GROUP) {
         this._selectedGroupId = id;
      }

      this.refreshLowerGrids();
   }

   private contextMenu(level: number, params: any): any[] {
      const self = this;
      const items: any[] = [];
      const rowData = (params && params.node && params.node.data) ? params.node.data as BucketRow : null;

      // An archived bucket is READ-ONLY (owner ruling 2026-06-10): no create/edit/move/transact/delete —
      // and no unarchive. Only the visibility toggle (and the harmless sizing pair) remains.
      if (rowData && rowData.isArchived) {
         items.push(this.showArchivedMenuItem());
         items.push('separator');
         items.push('autoSizeAll');
         items.push({ name: 'Size Columns To Fit', action: () => params.api.sizeColumnsToFit() });
         return items;
      }

      // Design lock (CR-20260930-001): a bucket under a combo that is under analysis has its DESIGN items
      // disabled (tooltip = the reason). Apply After State stays enabled — it shows the reason itself.
      const locked = !!rowData && self.isBucketLocked(rowData);
      const tip = locked ? self.lockTip : undefined;

      if (level === LEVEL_PORTFOLIO) {
         items.push({ name: 'Create Portfolio...', action: () => self.createPortfolio() });
      } else if (level === LEVEL_COMBO) {
         items.push({ name: 'Create Combo...', disabled: !self._selectedPortfolioId, action: () => self.createCombo() });
         if (rowData) {
            // Combo settings (CR-20260611-001): Default Qty (the Make-Transaction totals normalizer) +
            // auto-expire-at-reset. Combos saved before the backend field existed fall back to qty 1 until set.
            items.push({ name: 'Combo Settings...', action: () => self.editComboSettings(rowData) });
            items.push({
               name: 'Balance',
               subMenu: [
                  { name: 'Deposit', disabled: !rowData.hasStartingCapital || locked, tooltip: tip,
                     action: () => self.openAccountCash(rowData, 1) },
                  { name: 'Withdrawal', disabled: !rowData.hasStartingCapital || locked, tooltip: tip,
                     action: () => self.openAccountCash(rowData, 2) }
               ]
            });
            // CR-20260929-001 (ruling R11): the combo is the unit of analysis; open the Portfolio Hedging
            // Analysis panel on it (preselected).
            items.push({ name: 'Analyze Hedging...', action: () => self.analyzeHedging(rowData) });
            // Add RetroFill at Combo level too (vrequest 2026-06-10): no default target — every leg
            // must pick its Target Group explicitly (owner: require explicit enter).
            items.push({ name: 'Add Retro-fill...', disabled: locked, tooltip: tip, action: () => self.openRetroFill(rowData) });
         }
      } else if (level === LEVEL_GROUP) {
         const selectedCombo = self._selectedComboId ? self._buckets.find(b => b.bucketId === self._selectedComboId) : null;
         const comboLocked = !!selectedCombo && self.isBucketLocked(selectedCombo);
         items.push({
            name: 'Create Group...', disabled: !self._selectedComboId || comboLocked,
            tooltip: comboLocked ? self.lockTip : undefined, action: () => self.createGroup()
         });
         const gdata = rowData;
         if (gdata) {
            // The auto-provisioned Interest group carries the borrowed-amount + rate settings (WB-17);
            // every other group can move its positions elsewhere.
            if (gdata.name === 'Interest') {
               items.push({ name: 'Interest Settings...', action: () => self.editInterest(gdata) });
            } else {
               items.push({ name: 'Move Group To...', disabled: locked, tooltip: tip, action: () => self.moveGroup(gdata) });
               // Add RetroFill: a past-dated structure goes through the deliberate backdate dialog
               // (compose -> preview -> confirm) — never a casual forward entry.
               items.push({ name: 'Add Retro-fill...', disabled: locked, tooltip: tip, action: () => self.openRetroFill(gdata) });
            }
         }
      }
      // After State (CR — Collars After State): edit/save a desired end-state on any bucket; apply it when
      // the bucket (or a descendant group) has a saved after-state key.
      if (rowData) {
         // Make Transaction exists at Combo/Group scope only (owner ruling 2026-06-10) — the item is
         // ABSENT from portfolio rows, not grayed; Apply/Clear remain portfolio-capable.
         if (level !== LEVEL_PORTFOLIO) {
            items.push({ name: 'Make Transaction...', disabled: locked, tooltip: tip, action: () => self.openMakeTransaction(rowData) });
         }
         // Apply After State: on pending order blocks of the Portfolio Hedging Analysis (CR-20260930-001
         // M3) it opens the Export window in its acquire mode; on a saved plan (the old mechanism) the
         // old dialog. A bucket with BOTH (rare) gets the analysis window under the usual name and the
         // old dialog as "Apply Saved Plan..." [my call].
         const hasSavedPlan = self.bucketHasAfterState(rowData);
         const hasAnalysisPending = self.bucketHasAnalysisPending(rowData);
         if (hasAnalysisPending) {
            items.push({ name: 'Apply Fills...', action: () => self.openAcquire(rowData) });
         }
         if (hasSavedPlan) {
            items.push({
               name: hasAnalysisPending ? 'Apply Saved Plan...' : 'Apply Fills...',
               action: () => self.openApplyAfterState(rowData)
            });
            // Portfolio-level wipe: delete EVERY saved After State under the portfolio (no selectivity —
            // owner ruling, vrequest follow-up 2026-06-10), behind a confirmation.
            if (level === LEVEL_PORTFOLIO) {
               items.push({ name: 'Clear Saved State...', action: () => self.clearSavedState(rowData) });
            }
         }
         // Pending order blocks of the Portfolio Hedging Analysis (CR-20260930-001 M2): clear them here
         // (= the analysis panel's Revoke, block scope; an orphan of a dead analysis is removed). Applying
         // them with fills = Apply After State (M3), per block.
         if (hasAnalysisPending) {
            const n = self.analysisPendingUnder(rowData).length;
            items.push({
               name: 'Clear After State...', tooltip: n + ' pending order block(s) from the Portfolio Hedging Analysis',
               action: () => self.clearAnalysisAfterState(rowData)
            });
         }
      }
      if (rowData) {
         // Bucket-scoped Session History (owner request 2026-06-11): read-only view of this bucket's
         // subtree across its terminal's sessions — "the story of how we got here".
         items.push({ name: 'Show Session History...', action: () => self.showSessionHistory(level, rowData) });
         // Archive (owner rulings 2026-06-10): cascades over the subtree, auto-archives the closed
         // positions, blocked while open positions or a borrowing Interest group exist. One-way.
         items.push({ name: `Archive ${self.levelNoun(level)}...`, disabled: locked, tooltip: tip, action: () => self.archiveBucket(rowData) });
         items.push({ name: 'Delete', disabled: locked, tooltip: tip, action: () => self.deleteBucket(rowData) });
      }
      items.push('separator');
      items.push(this.showArchivedMenuItem());
      // Column sizing — the same always-available pair as the positions grid (owner, 2026-06-12).
      items.push('separator');
      items.push('autoSizeAll');
      items.push({ name: 'Size Columns To Fit', action: () => params.api.sizeColumnsToFit() });
      return items;
   }

   private levelNoun(level: number): string {
      return level === LEVEL_PORTFOLIO ? 'Portfolio' : (level === LEVEL_COMBO ? 'Combo' : 'Group');
   }

   // The show/hide toggle for archived buckets — one flipping item on every bucket grid's menu.
   private showArchivedMenuItem(): any {
      const self = this;
      return this.showArchivedBuckets
         ? { name: 'Hide Archived Buckets', action: () => self.toggleShowArchived(false) }
         : { name: 'Show Archived Buckets', action: () => self.toggleShowArchived(true) };
   }

   private toggleShowArchived(show: boolean): void {
      this.showArchivedBuckets = show;
      // Re-filter the grids off the in-memory data; pruneHiddenSelection (inside applyData) drops a
      // selection that just became hidden.
      this.applyData();
   }

   // Summon the Session History dialog scoped to one bucket. The popup is a SINGLETON hosted by the
   // host-menu (not this panel), so the panel publishes the scope over the message bus instead of
   // holding its own instance.
   private showSessionHistory(level: number, bucket: BucketRow): void {
      const scope: CollarsSessionHistoryScope = {
         level,
         bucketId: bucket.bucketId,
         bucketName: bucket.name,
         terminalId: bucket.ownerTerminalId
      };
      this._messageBus.publish({ topic: 'ShowCollarsSessionHistory', payload: scope });
   }

   /** CR-20260930-001 M4 (owner): a deep link from an analysis overdue card selects this combo -
    * the portfolio in the top grid, then the combo in the middle grid - so the pending after-state
    * blocks and their Clear/Acquire items are on screen. A miss retries (the list may still load). */
   private async applySelectComboRequest(request: OpenSubjectRequest, retries: number): Promise<void> {
      for (let i = 0; i <= retries; i++) {
         if (!this._buckets.length || i > 0) {
            await this.reload();
         }
         const combo = this._buckets.find(b => b.bucketId === request.comboId);
         if (combo) {
            this._selectedPortfolioId = combo.parentBucketId;
            this._selectedComboId = combo.bucketId;
            this._selectedGroupId = null;
            this.setChildRows(this.combosGrid, LEVEL_COMBO, combo.parentBucketId);
            this.setChildRows(this.groupsGrid, LEVEL_GROUP, combo.bucketId);
            this.reselect(this.portfoliosGrid, this._selectedPortfolioId);
            this.reselect(this.combosGrid, this._selectedComboId);
            this.refreshLowerGrids();
            return;
         }
         await new Promise(resolve => setTimeout(resolve, 500));
      }
      this._toastr.warning('The Requested Combo Is Not Available to You');
   }

   // ---- Create flows (custom dx-popup dialogs) ----

   /** CR-20260929-001: hand the combo to the Portfolio Hedging Analysis panel through the host's deep link. */
   private analyzeHedging(combo: CollarsBucketDto): void {
      const request: OpenSubjectRequest = {
         panel: 'PortfolioHedgingAnalysis',
         userId: '',
         portfolioId: combo.parentBucketId,
         comboId: combo.bucketId,
         expirationDayKey: null,
         actionItemId: null,
         targetPanelId: null,
         zoneDrift: null,
         at: Date.now()
      };
      this._messageBus.publish({ topic: OPEN_SUBJECT_REQUEST_TOPIC, payload: request });
   }

   private createPortfolio(): void {
      this.portfolioDialog = { visible: true, name: '', terminalId: null };
      this._changeDetector.detectChanges();
   }

   private createCombo(): void {
      const parent = this._buckets.find(b => b.bucketId === this._selectedPortfolioId);
      if (!parent) {
         this._toastr.warning('Select a Portfolio First');
         return;
      }
      this.comboDialog = { visible: true, name: '', template: 'HedgedPortfolio', portfolioBucketId: parent.bucketId, terminalId: parent.ownerTerminalId, defaultQty: 1, startingCapital: null };
      this._changeDetector.detectChanges();
   }

   private createGroup(): void {
      const parent = this._buckets.find(b => b.bucketId === this._selectedComboId);
      if (!parent) {
         this._toastr.warning('Select a Combo First');
         return;
      }
      this.groupDialog = { visible: true, name: '', comboBucketId: parent.bucketId, terminalId: parent.ownerTerminalId };
      this._changeDetector.detectChanges();
   }

   async onCreatePortfolio(): Promise<void> {
      const d = this.portfolioDialog;
      if (!d.name || !d.name.trim()) { this._toastr.warning('Enter a Portfolio Name'); return; }
      if (!d.terminalId) { this._toastr.warning('Select a Terminal'); return; }
      this.closePortfolioDialog();
      await this.run(new CreateCollarsPortfolio(d.name.trim(), d.terminalId));
   }

   async onCreateCombo(): Promise<void> {
      const d = this.comboDialog;
      if (!d.name || !d.name.trim()) { this._toastr.warning('Enter a Combo Name'); return; }
      if (!(d.startingCapital > 0)) { this._toastr.warning('Enter starting capital greater than zero'); return; }
      if (!(d.defaultQty >= 1)) { this._toastr.warning('Default Qty Must Be a Positive Number'); return; }
      this.closeComboDialog();
      const name = d.name.trim();
      const ok = await this.run(new CreateCollarsCombo(name, d.terminalId, d.portfolioBucketId, d.template, d.startingCapital));
      // A failed create must NOT write the setting — _buckets would still hold the stale tree, and a
      // pre-existing same-named combo would silently take the dialog's Default Qty.
      if (!ok) { return; }
      // The create op returns no bucket id; run() awaited reload(), so resolve the new combo off the fresh
      // bucket tree (parent + name) and persist its settings on the bucket row (auto-expire defaults OFF at
      // creation). With duplicate names under one portfolio the first match wins.
      const created = this._buckets.find(b =>
         b.level === LEVEL_COMBO && b.parentBucketId === d.portfolioBucketId && b.name === name);
      if (created) {
         await this.run(new SetCollarsComboSettings(d.terminalId, created.bucketId, Math.floor(d.defaultQty), false, d.startingCapital));
      } else {
         this._logger.error('Create combo: could not resolve the new combo to store its Default Qty', { name });
      }
   }

   async onCreateGroup(): Promise<void> {
      const d = this.groupDialog;
      if (!d.name || !d.name.trim()) { this._toastr.warning('Enter a group name'); return; }
      this.closeGroupDialog();
      await this.run(new CreateCollarsGroup(d.name.trim(), d.terminalId, d.comboBucketId), 'Operation failed', [d.comboBucketId]);
   }

   closePortfolioDialog(): void {
      this.portfolioDialog.visible = false;
      this._changeDetector.detectChanges();
   }

   closeComboDialog(): void {
      this.comboDialog.visible = false;
      this._changeDetector.detectChanges();
   }

   // ---- Combo settings (context menu "Combo Settings...", CR-20260611-001) ----
   // Backend-persisted on the bucket row; run() reloads the tree after save (the same command-then-reload
   // refresh the Interest settings use — there is no bucket push).

   private editComboSettings(combo: BucketRow): void {
      this.comboSettingsDialog = {
         visible: true,
         comboBucketId: combo.bucketId,
         comboName: combo.name,
         terminalId: combo.ownerTerminalId,
         qty: this._comboSettings.getDefaultQty(combo),
         autoExpire: !!combo.autoExpireOptions,
         startingCapital: combo.startingCapital,
         hasStartingCapital: !!combo.hasStartingCapital,
         accountValue: combo.accountValue,
         freeCash: combo.freeCash
      };
      this._changeDetector.detectChanges();
   }

   async onSaveComboSettings(): Promise<void> {
      const d = this.comboSettingsDialog;
      if (!(d.qty >= 1)) { this._toastr.warning('Default Qty Must Be a Positive Number'); return; }
      if (!d.hasStartingCapital && !(d.startingCapital > 0)) { this._toastr.warning('Enter starting capital greater than zero'); return; }
      this.closeComboSettingsDialog();
      await this.run(new SetCollarsComboSettings(d.terminalId, d.comboBucketId, Math.floor(d.qty), d.autoExpire, d.hasStartingCapital ? null : d.startingCapital));
   }

   openAccountCash(target: BucketRow, kind: number): void {
      const combo = this._buckets.find(b => b.bucketId === target.bucketId && b.level === LEVEL_COMBO);
      if (!combo) { this._toastr.warning('Select a combo first'); return; }
      if (!combo.hasStartingCapital) { this._toastr.warning('Set starting capital before changing this combo'); return; }
      this.accountCashDialog = { visible: true, kind, amount: null, comboBucketId: combo.bucketId, terminalId: combo.ownerTerminalId };
      this._changeDetector.detectChanges();
   }

   async onRecordAccountCash(): Promise<void> {
      const d = this.accountCashDialog;
      if (!(d.amount > 0)) { this._toastr.warning('Enter an amount greater than zero'); return; }
      d.visible = false;
      await this.run(new RecordCollarsAccountCash(d.terminalId, d.comboBucketId, d.kind, d.amount), 'Account cash operation failed', [d.comboBucketId]);
   }

   closeAccountCashDialog(): void { this.accountCashDialog.visible = false; this._changeDetector.detectChanges(); }

   closeComboSettingsDialog(): void {
      this.comboSettingsDialog.visible = false;
      this._changeDetector.detectChanges();
   }

   closeGroupDialog(): void {
      this.groupDialog.visible = false;
      this._changeDetector.detectChanges();
   }

   // ---- Interest settings (WB-17; redesigned 2026-06-03): borrowed + rate + days-in-year + schedule ----
   // Interest is a DISCRETE scheduled charge (daily/weekly at a configured local time), posted by the
   // backend job — there is no per-second ticker here. The Interest item shows the posted total, static
   // between charges (refreshed on the ledger push in onLedgerChanged).

   private editInterest(group: BucketRow): void {
      const tz = group.chargeTimeZoneId || this._timestamps.getDefaultTimezone('UTC');
      const country = group.chargeCountry || this.countryForZone(tz);
      this.setTimezoneItemsForCountry(country);   // populate the zone list so the prefilled tz shows
      this.interestDialog = {
         visible: true,
         groupBucketId: group.bucketId,
         terminalId: group.ownerTerminalId,
         groupName: group.name,
         borrowed: group.borrowedAmount || 0,
         rate: group.interestRate || 0,                  // decimal fraction; the % mask shows it as a percent
         daysInYear: group.daysInYear || 365,
         chargeInterval: group.chargeInterval || 0,
         chargeDayOfWeek: (group.chargeDayOfWeek === null || group.chargeDayOfWeek === undefined) ? 1 : group.chargeDayOfWeek,
         timeOfDay: group.chargeTimeOfDay || '17:00',
         country,
         timeZoneId: tz
      };
      this._changeDetector.detectChanges();
   }

   async onSaveInterest(): Promise<void> {
      const d = this.interestDialog;
      const borrowed = d.borrowed || 0;
      const rate = d.rate || 0;   // decimal fraction (the % mask already converts the entered percent)
      const daysInYear = d.daysInYear || 365;
      if (borrowed < 0) { this._toastr.warning('Borrowed amount cannot be negative'); return; }
      if (rate < 0) { this._toastr.warning('Interest rate cannot be negative'); return; }
      if (daysInYear <= 0) { this._toastr.warning('Days in year must be positive'); return; }
      if (borrowed > 0 && !d.timeZoneId) { this._toastr.warning('Select a timezone'); return; }
      if (borrowed > 0 && !d.timeOfDay) { this._toastr.warning('Enter a charge time'); return; }
      // Weekday only matters for Weekly; send null for Daily.
      const weekday = d.chargeInterval === 1 ? d.chargeDayOfWeek : null;
      // Country is an explicit selection (label only); fall back to deriving it from the zone.
      const country = d.country || this.countryForZone(d.timeZoneId);
      this.closeInterestDialog();
      await this.run(new SetCollarsInterestSettings(
         d.terminalId, d.groupBucketId, borrowed, rate, daysInYear,
         d.chargeInterval, weekday, d.timeOfDay, d.timeZoneId, country));
   }

   closeInterestDialog(): void {
      this.interestDialog.visible = false;
      this._changeDetector.detectChanges();
   }

   // Country label for an IANA zone (zones are grouped by country); used to prefill/fallback the label.
   private countryForZone(zoneId: string): string {
      if (!zoneId) { return null; }
      const fam = TIME_ZONE_FAMILIES.find(x => x.timeZoneDescriptors.some(y => y.nodaTimeZoneId === zoneId));
      return fam ? fam.displayName : null;
   }

   // Country change repopulates the timezone list and clears the now-stale zone (raw-select wiring).
   onInterestCountryChange(country: string): void {
      this.interestDialog.country = country;
      this.setTimezoneItemsForCountry(country);
      this.interestDialog.timeZoneId = null;
      this._changeDetector.detectChanges();
   }

   // Interval change must repaint explicitly: the 'Day of week' row's *ngIf reads chargeInterval, and
   // the panel view is DETACHED — a bare two-way binding write never showed/hid the row, so a Weekly
   // pick could silently save the prefilled default weekday.
   onInterestIntervalChange(interval: number): void {
      this.interestDialog.chargeInterval = interval;
      this._changeDetector.detectChanges();
   }

   private setTimezoneItemsForCountry(country: string): void {
      const fam = country ? TIME_ZONE_FAMILIES.find(x => x.displayName === country) : null;
      this.timezoneItems = fam ? fam.timeZoneDescriptors : [];
   }

   // ---- Move (transfer) a position or a whole group to another bucket ----

   // Context menu on a Portfolio Items row — only real positions move (the synthetic Interest item is skipped).
   private positionContextMenu(params: any): any[] {
      const self = this;
      const data = (params && params.node && params.node.data) ? params.node.data as PendingFillRow : null;
      const items: any[] = [];
      if (data && data.isPendingFill) {
         items.push({ name: 'Apply Fill...', action: () => self.openAcquireForPendingFill(data) });
      }
      // Actions apply to real, still-active positions; an archived row is terminal (no move/settle/archive).
      if (data && !data.isPendingFill && !data.isPendingFillPlaceholder && !data.isInterest && !data.isArchived) {
         // Design lock (CR-20260930-001): a position of a combo under analysis takes no manual design action.
         const locked = self.isPositionLocked(data);
         const tip = locked ? self.lockTip : undefined;
         // Adjust = open the Make Transaction dialog on this position's Group (same as double-click).
         items.push({ name: 'Adjust Position', disabled: locked, tooltip: tip, action: () => self.adjustPosition(data) });
         items.push({ name: 'Move To...', disabled: locked, tooltip: tip, action: () => self.movePosition(data) });
         items.push({ name: 'Override...', disabled: locked, tooltip: tip, action: () => self.overridePosition(data) });
         // Option legs can be settled at expiry (close worthless, or assign/exercise to the underlying).
         if (data.kind === KIND_OPTION && data.netPosition !== 0) {
            items.push({ name: 'Settle at Expiry...', action: () => self.settleExpiry(data) });
         }
         // Archive a CLOSED position: it leaves Active for the Archived section (FR-M3-7). Disabled while
         // open — only flat positions archive (the next trade on the discriminator opens a new position).
         items.push({ name: 'Archive Position', disabled: data.netPosition !== 0 || locked, tooltip: tip, action: () => self.archivePosition(data) });
         // A pending order of the Portfolio Hedging Analysis on THIS instrument (CR-20260930-001 M3): apply
         // it from the position row — the dialog opens on the group, narrowed to that order.
         const onThis = self.analysisBlocksOnPosition(data).length;
         if (onThis > 0) {
            items.push({
               name: 'Apply After State...', tooltip: onThis + ' pending order(s) of the Portfolio Hedging Analysis on this position',
               action: () => self.openAcquireForPosition(data)
            });
         }
      }
      // The synthetic Interest item (owner request 2026-06-12): a manual, on-demand interest charge —
      // the testing/operator alternative to waiting for the scheduled fire.
      if (data && !data.isPendingFill && data.isInterest && !data.isArchived) {
         items.push({ name: 'Manual Charge...', action: () => self.manualInterestCharge(data) });
      }
      // Column sizing — the ETS-CORE grid pattern (portfolio-items/ledger): the built-in 'autoSizeAll'
      // plus the codebase's standard Size-To-Fit entry. Always available, even on empty/group rows.
      if (items.length > 0) { items.push('separator'); }
      items.push('autoSizeAll');
      items.push({ name: 'Size Columns To Fit', action: () => params.api.sizeColumnsToFit() });
      return items;
   }

   private movePosition(position: CollarsPositionDto): void {
      this.moveDialog = {
         visible: true, scope: 'position', title: 'Move Position',
         sourceId: position.positionId, sourceGroupId: position.groupId, terminalId: position.terminalId,
         mode: 1, targetPortfolioId: null, targetComboId: null, targetGroupId: null
      };
      this._changeDetector.detectChanges();
   }

   // Archive a closed position (WB-18, FR-M3-7): confirm, then send ArchiveCollarsPosition. The backend
   // freezes Realized + stamps ArchivedDate and pushes the new state; the row moves to the Archived
   // section. One-way — the next trade on the same instrument/bucket opens a brand-new active position.
   private async archivePosition(position: CollarsPositionDto): Promise<void> {
      if (!position) { return; }
      if (position.netPosition !== 0) { this._toastr.warning('Open position cannot be archived; close it first'); return; }

      this.confirmTitle = 'Archive Position';
      this._changeDetector.detectChanges();

      try {
         await this.confirmationDialog.show([
            `Archive '${this.dispName(position.tradingInstrumentCode, false)}'? ` +
            `It moves to the Archived section; the next trade on it opens a new position.`
         ]);
      } catch (e) {
         return; // user chose No / dismissed
      }

      await this.run(new ArchiveCollarsPosition(position.terminalId, position.positionId));
   }

   // Manual interest charge (owner request 2026-06-12): right-click the synthetic Interest item to post
   // an EXTRA charge NOW — whole days elapsed since the last charge; one full period for a first charge.
   // The scheduled charge is untouched. The confirmation dialog shows the BACKEND-previewed period and
   // amount (PreviewCollarsInterestCharge — the same computation the charge runs; the fact-log "last
   // charge" and the emulator-aware clock live backend-side, so the frontend never re-derives them).
   // Nothing due -> the dialog is skipped and the backend's plain-language reason shows as a toast.
   private async manualInterestCharge(position: CollarsPositionDto): Promise<void> {
      if (!position) { return; }

      const group = this._buckets.find(b => b.bucketId === position.groupId);
      if (!group) {
         this._toastr.warning('Interest group not found — reload the panel');
         return;
      }

      let preview: CollarsInterestChargePreviewDto;
      try {
         preview = await this._shellClient.processQuery<CollarsInterestChargePreviewDto>(
            new PreviewCollarsInterestCharge(position.terminalId, position.groupId));
      } catch (e) {
         this._logger.error('Manual interest charge preview failed', { error: e.stack || e });
         this._toastr.error('Manual interest charge preview failed');
         return;
      }

      if (!preview || !preview.isDue) {
         this._toastr.warning((preview && preview.reason) || 'Nothing due');
         return;
      }

      this.manualChargeDialog = {
         visible: true,
         periodLabel: `${preview.chargeDays} day${preview.chargeDays === 1 ? '' : 's'}`,
         // The amount is NEGATIVE (a financing cost); the dialog shows the magnitude being charged.
         amountLabel: '$' + Math.abs(preview.amount).toLocaleString('en-US',
            { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
         terminalId: position.terminalId,
         groupBucketId: position.groupId
      };
      this._changeDetector.detectChanges();
   }

   // Yes on the confirmation dialog. The backend RECOMPUTES at commit (it stays the authority): if a day
   // boundary or another charge slipped in between, the posted amount follows the backend, not the label.
   async onConfirmManualCharge(): Promise<void> {
      const d = this.manualChargeDialog;
      this.closeManualChargeDialog();
      // Cause-neutral hint: the same toast fires for EVERY rejection (a between-clicks race, transport
      // errors) — the exact reason is in the shell log, never in the WCF fault.
      await this.run(new ChargeCollarsInterest(d.terminalId, d.groupBucketId),
         'Manual interest charge failed — possibly nothing is due anymore; the shell log has the exact reason');
   }

   closeManualChargeDialog(): void {
      this.manualChargeDialog.visible = false;
      this._changeDetector.detectChanges();
   }

   // Double-click a Portfolio Items row → open the Make Transaction dialog scoped to that position's
   // Group. The end-state editor resolves the scope's current positions itself, so the clicked position
   // arrives as an editable leg. Replaces the retired "Add Position" blotter (owner ruling 2026-06-10).
   // Interest items and archived rows are ignored.
   private onPositionRowDoubleClicked(e: any): void {
      const data = (e && e.data) ? e.data as PendingFillRow : null;
      if (!data) { return; }            // group/section header row
      if (data.isPendingFill) { this.openAcquireForPendingFill(data); return; }
      if (data.isPendingFillPlaceholder) { return; }
      if (data.isInterest) { return; }  // synthetic Interest row — not a tradable position
      if (data.isArchived) { return; }  // archived rows are terminal
      if (this.isPositionLocked(data)) {   // design-locked (CR-20260930-001): say why instead of opening the dialog
         this._toastr.warning(this.lockTip);
         return;
      }
      this.adjustPosition(data);
   }

   private async adjustPosition(position: CollarsPositionDto): Promise<void> {
      const group = this._buckets.find(b => b.bucketId === position.groupId);
      if (!group) { this._toastr.warning('Cannot find the position\'s group'); return; }
      await this.openMakeTransaction(this.toRow(group));
   }

   private moveGroup(group: BucketRow): void {
      this.moveDialog = {
         visible: true, scope: 'group', title: `Move Group "${group.name}"`,
         sourceId: group.bucketId, sourceGroupId: group.bucketId, terminalId: group.ownerTerminalId,
         mode: 1, targetPortfolioId: null, targetComboId: null, targetGroupId: null
      };
      this._changeDetector.detectChanges();
   }

   // Target cascade, built from the loaded bucket tree, scoped to the source terminal (same-terminal
   // only). Archived buckets are never offered — read-only (the backend rejects them anyway).
   get moveTargetPortfolios(): CollarsBucketDto[] {
      const tid = this.moveDialog.terminalId;
      return this._buckets.filter(b => b.level === LEVEL_PORTFOLIO && b.ownerTerminalId === tid && !b.isArchived);
   }

   get moveTargetCombos(): CollarsBucketDto[] {
      const pid = this.moveDialog.targetPortfolioId;
      return pid ? this._buckets.filter(b => b.level === LEVEL_COMBO && b.parentBucketId === pid && !b.isArchived) : [];
   }

   get moveTargetGroups(): CollarsBucketDto[] {
      const cid = this.moveDialog.targetComboId;
      if (!cid) { return []; }
      // Exclude the source group itself, the Interest group (it holds no positions), and archived groups.
      return this._buckets.filter(b => b.level === LEVEL_GROUP && b.parentBucketId === cid
         && b.bucketId !== this.moveDialog.sourceGroupId && b.name !== 'Interest' && !b.isArchived);
   }

   onMoveTargetPortfolioChange(portfolioId: string): void {
      this.moveDialog.targetPortfolioId = portfolioId;
      this.moveDialog.targetComboId = null;
      this.moveDialog.targetGroupId = null;
      this._changeDetector.detectChanges();
   }

   onMoveTargetComboChange(comboId: string): void {
      this.moveDialog.targetComboId = comboId;
      this.moveDialog.targetGroupId = null;
      this._changeDetector.detectChanges();
   }

   async onMove(): Promise<void> {
      const d = this.moveDialog;
      if (!d.targetGroupId) { this._toastr.warning('Select a target group'); return; }
      if (d.targetGroupId === d.sourceGroupId) { this._toastr.warning('Pick a group other than the source'); return; }
      this.closeMoveDialog();
      // Neither out of nor into a combo under analysis (CR-20260930-001).
      const guard = [d.sourceGroupId, d.targetGroupId];
      if (d.scope === 'group') {
         await this.run(new TransferCollarsGroup(d.terminalId, d.sourceId, d.targetGroupId, d.mode), 'Operation failed', guard);
      } else {
         await this.run(new TransferCollarsPosition(d.terminalId, d.sourceId, d.targetGroupId, d.mode), 'Operation failed', guard);
      }
   }

   closeMoveDialog(): void {
      this.moveDialog.visible = false;
      this._changeDetector.detectChanges();
   }

   // ---- Override a position's quantity and/or average price (enter-only, a manual correction) ----

   private overridePosition(position: CollarsPositionDto): void {
      this.overrideDialog = {
         visible: true, title: 'Override Position', terminalId: position.terminalId,
         positionId: position.positionId, groupId: position.groupId, symbol: this.dispName(position.tradingInstrumentCode, false),
         netPosition: position.netPosition, avgPx: position.avgPx
      };
      this._changeDetector.detectChanges();
   }

   // Either field is optional: a cleared (null) box leaves that value unchanged. The backend enforces the
   // net<->avg rule (resolved net != 0 iff resolved avg != 0) and surfaces any error via run().
   async onOverride(): Promise<void> {
      const d = this.overrideDialog;
      const net = (d.netPosition === null || d.netPosition === undefined) ? null : Math.round(Number(d.netPosition));
      const avg = (d.avgPx === null || d.avgPx === undefined) ? null : Number(d.avgPx);
      if (net === null && avg === null) {
         this._toastr.warning('Enter a new quantity and/or average price to override');
         return;
      }
      this.closeOverrideDialog();
      await this.run(new OverrideCollarsPosition(d.terminalId, d.positionId, net, avg), 'Operation failed', [d.groupId]);
   }

   closeOverrideDialog(): void {
      this.overrideDialog.visible = false;
      this._changeDetector.detectChanges();
   }

   // ---- Settle an option at expiry (enter-only): close the option leg, optionally spawn the underlying ----

   private settleExpiry(position: CollarsPositionDto): void {
      const t = parseOptionTicker(position.tradingInstrumentCode);
      // Index options (SPX, SPXW, ...) are cash-settled — never physically (the index isn't a tradable
      // asset). Detected from the option ticker, the same check used everywhere else (European vs American).
      const cashSettled = !!isCashSettledOptionTicker(position.tradingInstrumentCode);
      this.settleDialog = {
         visible: true,
         optionPositionId: position.positionId,
         terminalId: position.terminalId,
         optionName: this.dispName(position.tradingInstrumentCode, false),
         net: position.netPosition,
         cashSettled,
         strike: t ? t.strike : null,
         optionType: t ? t.type : '',
         expiration: t ? t.expiration : '',
         settlementPrice: null,
         loading: true, levelResolved: false, levelSource: '', settleTimeUtc: null,
         isInTheMoney: false, intrinsic: null, moneynessLabel: '',
         outcomeLabel: '', realizedPnL: null, premiumAmount: null,
         spawnsUnderlying: false, underlyingCode: '', underlyingQty: 0, underlyingSide: '',
         description: ''
      };
      this._changeDetector.detectChanges();
      // Resolve the settlement price for the expiration day + derive the outcome (backend single source).
      this.loadExpiryPreview(null);
   }

   // Fetch the backend preview: resolves the settlement level (override -> mark -> fetch) and DERIVES the
   // outcome. `override` null = let the backend resolve it; a value = the user typed/overrode the level.
   private async loadExpiryPreview(override: number | null): Promise<void> {
      const d = this.settleDialog;
      const positionId = d.optionPositionId;
      d.loading = true;
      this._changeDetector.detectChanges();
      try {
         const p = await this._shellClient.processQuery<CollarsOptionExpiryPreviewDto>(
            new PreviewCollarsOptionExpiry(d.terminalId, positionId, override));
         // Guard against a stale reply if the user reopened the dialog on another position meanwhile.
         if (!this.settleDialog.visible || this.settleDialog.optionPositionId !== positionId) { return; }
         const cur = this.settleDialog;
         cur.loading = false;
         cur.levelResolved = p.levelResolved;
         cur.levelSource = p.levelSource;
         cur.settleTimeUtc = p.settleTimeUtc;
         cur.isInTheMoney = p.isInTheMoney;
         cur.intrinsic = p.intrinsic;
         cur.moneynessLabel = p.moneynessLabel;
         cur.outcomeLabel = p.outcomeLabel;
         cur.realizedPnL = p.realizedPnL;
         cur.premiumAmount = p.premiumAmount;
         cur.spawnsUnderlying = p.spawnsUnderlying;
         cur.underlyingCode = p.underlyingCode;
         cur.underlyingQty = p.underlyingQty;
         cur.underlyingSide = p.underlyingSide;
         // Only adopt the resolved level into the editable box when the user has not supplied one (override
         // null) — otherwise keep what they typed. A resolved level pre-fills the box for confirmation.
         if (override === null && p.levelResolved) { cur.settlementPrice = p.settlementLevel; }
      } catch (e) {
         if (this.settleDialog.visible && this.settleDialog.optionPositionId === positionId) {
            this.settleDialog.loading = false;
            this.settleDialog.description = 'Could not load the expiry preview.';
         }
         this._logger.error('Collars settle-expiry preview failed', { error: e });
      }
      this._changeDetector.detectChanges();
   }

   // User edited the settlement level -> re-derive (debounced). Programmatic writes (the pre-fill above)
   // carry no `event` and are ignored, so adopting a resolved level does not loop.
   onSettlementLevelChanged(e: any): void {
      if (!e || !e.event) { return; }
      const level = (typeof e.value === 'number' && isFinite(e.value)) ? e.value : null;
      // Write + repaint NOW: the Settle gate reads settlementPrice and the panel view is detached —
      // without this the button stayed disabled until the debounced preview round-trip repainted.
      // (Idempotent with the banana write; ordering between the two DX emissions is not guaranteed.)
      this.settleDialog.settlementPrice = level;
      this._changeDetector.detectChanges();
      if (this._expiryPreviewTimer) { clearTimeout(this._expiryPreviewTimer); }
      this._expiryPreviewTimer = setTimeout(() => this.loadExpiryPreview(level), 400);
   }

   async onSettleExpiry(): Promise<void> {
      const d = this.settleDialog;
      if (d.settlementPrice == null || !isFinite(d.settlementPrice)) {
         this._toastr.warning('Enter the underlying settlement price'); return;
      }
      this.closeSettleDialog();
      // The backend derives the outcome from the level; the dialog sends only the level it displayed.
      await this.run(new SettleCollarsOptionExpiry(d.terminalId, d.optionPositionId, d.settlementPrice));
   }

   // The settlement instant (4:20 PM ET, sent as UTC), shown in the app display timezone like every other
   // Collars timestamp. Empty until the backend returns a real settle date (hidden for an unknown contract).
   get settleTimeDisplay(): string {
      const d = this.settleDialog.settleTimeUtc;
      if (!d || d.getFullYear() < 1990) { return ''; }
      return this._timestamps.getZonedFormattedDateTime(d, 'dd-MMM-yyyy hh:mm a');
   }

   closeSettleDialog(): void {
      if (this._expiryPreviewTimer) { clearTimeout(this._expiryPreviewTimer); this._expiryPreviewTimer = null; }
      this.settleDialog.visible = false;
      this._changeDetector.detectChanges();
   }

   private async deleteBucket(bucket: BucketRow): Promise<void> {
      const type = bucket.level === LEVEL_COMBO ? 'combo' : (bucket.level === LEVEL_GROUP ? 'group' : 'portfolio');
      let cascade = '';
      if (bucket.level === LEVEL_PORTFOLIO) { cascade = ' and its combos and groups'; }
      else if (bucket.level === LEVEL_COMBO) { cascade = ' and its groups'; }

      this.confirmTitle = 'Delete ' + type.charAt(0).toUpperCase() + type.slice(1);
      this._changeDetector.detectChanges();

      try {
         await this.confirmationDialog.show([`Are you sure you want to delete ${type} '${bucket.name}'${cascade}?`]);
      } catch (e) {
         return; // user chose No / dismissed
      }

      await this.run(new DeleteCollarsBucket(bucket.bucketId, bucket.ownerTerminalId), 'Operation failed', [bucket.bucketId]);
   }

   // ---- Archive a bucket subtree (owner rulings 2026-06-10) ----

   // All bucket ids of the subtree rooted at rootId (inclusive) — mirrors the backend subtree walk.
   private subtreeBucketIds(rootId: string): string[] {
      const result: string[] = [];
      const frontier: string[] = [rootId];
      while (frontier.length > 0) {
         const current = frontier.pop();
         result.push(current);
         this._buckets.forEach(b => {
            if (b.parentBucketId === current) { frontier.push(b.bucketId); }
         });
      }
      return result;
   }

   private async archiveBucket(bucket: BucketRow): Promise<void> {
      if (bucket.isArchived) { return; }   // menu already hides this; double safety

      const subtree = this.subtreeBucketIds(bucket.bucketId);
      const inSubtree = (p: CollarsPositionDto) =>
         subtree.indexOf(p.portfolioId) >= 0 || subtree.indexOf(p.comboId) >= 0 || subtree.indexOf(p.groupId) >= 0;

      // Pre-validate the two blocking rules CLIENT-SIDE so the user sees the explanatory reason
      // (decision: "block but show explaining message" — run()'s failure toast is generic). The backend
      // re-checks both and stays the authority.
      const real = this._positions.filter(p => !p.isInterest && inSubtree(p));
      const open = real.filter(p => !p.isArchived && p.netPosition !== 0);
      if (open.length > 0) {
         this._toastr.warning(`Cannot archive '${bucket.name}': ${open.length} open position(s) in it — close them first`);
         return;
      }
      const borrowing = this._buckets.find(b => subtree.indexOf(b.bucketId) >= 0 && b.borrowedAmount > 0);
      if (borrowing) {
         this._toastr.warning(`Cannot archive '${bucket.name}': group '${borrowing.name}' has a borrowed amount — repay it (set borrowed to 0) first`);
         return;
      }

      const noun = this.levelNoun(bucket.level);
      let cascade = '';
      if (bucket.level === LEVEL_PORTFOLIO) { cascade = ' and its combos and groups'; }
      else if (bucket.level === LEVEL_COMBO) { cascade = ' and its groups'; }
      const toSweep = real.filter(p => !p.isArchived).length;   // closed positions auto-archived with it

      this.confirmTitle = 'Archive ' + noun;
      this._changeDetector.detectChanges();
      try {
         await this.confirmationDialog.show([
            `Archive ${noun.toLowerCase()} '${bucket.name}'${cascade}?` +
            (toSweep > 0 ? ` ${toSweep} closed position(s) will be archived with it.` : '') +
            ` This cannot be undone — an archived bucket is read-only.`
         ]);
      } catch (e) {
         return; // user chose No / dismissed
      }

      await this.run(new ArchiveCollarsBucket(bucket.ownerTerminalId, bucket.bucketId), 'Operation failed', [bucket.bucketId]);
   }

   // ---- After State (Make Transaction / Apply After State / RetroFill + green indicator) ----

   // Open the Make-Transaction dialog for a bucket (Portfolio / Combo / Group). The dialog edits the desired
   // end-state in-line and either SAVES it to user settings or DIRECT-COMMITS the derived transaction now.
   // Config = the locked CollarsMakeTransactionConfig (scope bucket id + level + owning terminal); the dialog
   // resolves the scope's current positions itself. On resolve, refresh so the book + green indicator match.
   private async openMakeTransaction(bucket: BucketRow): Promise<void> {
      // Read-only gate: the context menu never offers this on an archived row, but adjustPosition also
      // routes here — keep one defensive check at the single entry point.
      if (this.isBucketLocked(bucket)) {   // design-locked (CR-20260930-001): one defensive check at the single entry point
         this._toastr.warning(this.lockTip);
         return;
      }
      if (bucket.isArchived) {
         this._toastr.warning(`'${bucket.name}' is archived and read-only`);
         return;
      }
      const cfg: CollarsMakeTransactionConfig = {
         scopeBucketId: bucket.bucketId,
         level: bucket.level,
         terminalId: bucket.ownerTerminalId
      };
      const result = await this.makeTransactionDialog.show(cfg);
      if (!result) { return; }
      if (result.committed) {
         // Booked legs => positions/ledger changed. reload() re-rolls + re-runs applyData(), which also
         // refreshes the after-state indicator snapshot — so a commit-then-discard repaints in one pass.
         await this.reload();
      } else if (result.saved) {
         // SAVE only touched user settings (no fills) — positions are unchanged, so just recompute the
         // indicator snapshot and re-paint the bucket grids (no server round-trip).
         this.applyData();
      }
   }

   // Open the Apply-After-State dialog for a bucket that (or whose descendant group) has a saved end-state.
   // On apply, both the book (booked legs) and the after-state keys (consumed) change — a full reload re-rolls
   // positions/ledger AND re-runs applyData(), which refreshes the green-indicator snapshot.
   private async openApplyAfterState(bucket: BucketRow): Promise<void> {
      const cfg: CollarsApplyAfterStateConfig = {
         scopeBucketId: bucket.bucketId,
         level: bucket.level,
         terminalId: bucket.ownerTerminalId
      };
      const result = await this.applyAfterStateDialog.show(cfg);
      await this.afterApplyDialog(result);
   }

   /**
    * Apply After State on the pending ORDER BLOCKS of the Portfolio Hedging Analysis (CR-20260930-001 M3):
    * the Analysis panel's Export window in its acquire mode (owner 2026-09-30), scoped to the bucket.
    */
   private async openAcquire(bucket: BucketRow): Promise<void> {
      const result = await this.acquireDialog.show({ scopeBucketId: bucket.bucketId, level: bucket.level });
      await this.afterApplyDialog(result);
   }

   /**
    * The same on a SINGLE ELEMENT (spec §6.5 "a single element shows one block"): a position row whose
    * group carries pending analysis blocks on that instrument opens the window on its group, narrowed
    * to those blocks.
    */
   private async openAcquireForPosition(position: CollarsPositionDto): Promise<void> {
      const group = position && position.groupId ? this._buckets.find(b => b.bucketId === position.groupId) : null;
      if (!group) { return; }
      const blockIds = this.analysisBlocksOnPosition(position).map(pb => pb.block.blockId);
      if (blockIds.length === 0) { return; }
      const result = await this.acquireDialog.show({ scopeBucketId: group.bucketId, level: group.level, analysisBlockIds: blockIds });
      await this.afterApplyDialog(result);
   }

   /** A pending row opens acquisition for its whole order block (all option legs together). */
   private async openAcquireForPendingFill(row: PendingFillRow): Promise<void> {
      if (!row || !row.groupId || !row.analysisBlockId) { return; }
      const group = this._buckets.find(b => b.bucketId === row.groupId);
      if (!group) { return; }
      const result = await this.acquireDialog.show({
         scopeBucketId: group.bucketId,
         level: group.level,
         analysisBlockIds: [row.analysisBlockId]
      });
      await this.afterApplyDialog(result);
   }

   /** The pending analysis blocks that trade this position's instrument in its group. */
   private analysisBlocksOnPosition(position: CollarsPositionDto): AnalysisPendingBlockDto[] {
      if (!position || !position.groupId) { return []; }
      return (this._analysisPendingByGroup[position.groupId] || []).filter(pb =>
         ((pb.block && pb.block.changes) || []).some(c =>
            c.groupBucketId === position.groupId && c.tradingInstrumentCode === position.tradingInstrumentCode));
   }

   private async afterApplyDialog(result: { applied: boolean; discarded?: boolean }): Promise<void> {
      if (result && result.applied) {
         await this.reload();
         this.autoSizePortfolioItems();
      } else if (result && result.discarded) {
         // Discard-only session: no fills booked, but after-state key(s) were deleted — refresh the
         // green-indicator snapshot + bucket grids (settings-only change, no server round-trip). A cleared
         // analysis block (M3) is a server change: re-read the pending set so its earmark goes too.
         await this.refreshAnalysisLocks();
         this.applyData();
      }
   }

   // Open the backdate (retro-fill) dialog — the multi-leg DATED-entry dialog (CR-20260610-001): a
   // deliberate compose -> preview -> confirm path (a backdate is a serious recompute, never a casual
   // entry; the whole structure books atomically). Reached from a GROUP row (clicked group pre-selected
   // as each leg's default target) or a COMBO row (vrequest 2026-06-10: NO default — every leg must
   // pick its Target Group explicitly). Routing targets = the Combo's child Groups, Interest excluded.
   private openRetroFill(bucket: BucketRow): void {
      const isGroup = bucket.level === LEVEL_GROUP;
      const combo = isGroup ? this._buckets.find(x => x.bucketId === bucket.parentBucketId) : bucket;
      const portfolio = combo ? this._buckets.find(x => x.bucketId === combo.parentBucketId) : null;
      const routingGroups = combo
         ? this._buckets
            .filter(b => b.level === LEVEL_GROUP && b.parentBucketId === combo.bucketId
               && b.name !== 'Interest' && !b.isArchived)   // archived = read-only, never a retro target
            .map(b => ({ bucketId: b.bucketId, name: b.name }))
         : (isGroup ? [{ bucketId: bucket.bucketId, name: bucket.name }] : []);

      if (routingGroups.length === 0) {
         this._toastr.warning('No groups to backdate into — create a group first');
         return;
      }

      const cfg: CollarsBackdateConfig = {
         terminalId: bucket.ownerTerminalId,
         groupBucketId: isGroup ? bucket.bucketId : null,
         groupName: isGroup ? bucket.name : '',
         comboName: combo ? combo.name : '',
         portfolioName: portfolio ? portfolio.name : '',
         routingGroups
      };
      this.backdateDialog.show(cfg).then(committed => {
         if (committed) { this.reload(); }
      });
   }

   // Wipe EVERY saved After State under a portfolio (all descendant groups' keys), with confirmation.
   // Settings-only change — no booking, no server reload; applyData() refreshes the indicator + grids.
   private async clearSavedState(portfolio: BucketRow): Promise<void> {
      this.confirmTitle = 'Clear Saved State';
      this._changeDetector.detectChanges();
      try {
         await this.confirmationDialog.show([
            `Clear ALL saved After States under portfolio '${portfolio.name}'? ` +
            `Every saved plan is deleted and never applied.`
         ]);
      } catch (e) {
         return; // user chose No / dismissed
      }

      const comboIds = this._buckets
         .filter(b => b.level === LEVEL_COMBO && b.parentBucketId === portfolio.bucketId)
         .map(b => b.bucketId);
      const groupIds = this._buckets
         .filter(b => b.level === LEVEL_GROUP && comboIds.indexOf(b.parentBucketId) >= 0)
         .map(b => b.bucketId);
      const saved = this._afterStateService.listAfterStateGroupIds()
         .filter(id => groupIds.indexOf(id) >= 0);

      for (let i = 0; i < saved.length; i++) {
         await this._afterStateService.discard(saved[i]);
      }

      this._logger.info(`Collars clear saved state -> portfolio=${portfolio.bucketId} cleared=${saved.length}`);
      this._toastr.success('Saved After States cleared');
      this.applyData();
      this._changeDetector.detectChanges();
   }

   // Does this bucket cascade green? A Group is green if it has its own after-state key; a Combo is green if
   // any of its child Groups is green; a Portfolio is green if any of its descendant Groups is green. Reads
   // the per-pass snapshot (_afterStateGroupIds, refreshed in applyData) — never re-enumerates settings here.
   private bucketHasAfterState(bucket: BucketRow): boolean {
      if (!bucket) { return false; }
      return this.bucketHasAfterStateWith(bucket, this._afterStateGroupIds);
   }

   /** The analysis earmark (M2): a group with a pending analysis block; a combo/portfolio when any descendant group has one. */
   private bucketHasAnalysisPending(bucket: CollarsBucketDto): boolean {
      if (!bucket) { return false; }
      const set: { [groupId: string]: true } = {};
      Object.keys(this._analysisPendingByGroup).forEach(g => { set[g] = true; });
      return this.bucketHasAfterStateWith(bucket, set);
   }

   private bucketHasAfterStateWith(bucket: CollarsBucketDto, set: { [groupId: string]: true }): boolean {
      if (bucket.level === LEVEL_GROUP) {
         // An archived group's saved key is dead weight: the backend rejects fills into an archived
         // bucket, so its plan can never be applied — stop cascading the green badge (and the Apply
         // menu) for it. The orphaned key remains deletable via the portfolio's Clear Saved State.
         return !bucket.isArchived && !!set[bucket.bucketId];
      }
      // Combo / Portfolio: green if any descendant group is green.
      const childLevel = bucket.level + 1;
      const children = this._buckets.filter(b => b.level === childLevel && b.parentBucketId === bucket.bucketId);
      return children.some(child => this.bucketHasAfterStateWith(child, set));
   }

   // Returns whether the command succeeded (callers that chain follow-up work — e.g. the post-create
   // Default Qty write — must not act on a failed command; most callers ignore the result).
   // failureMessage lets a caller show a command-specific toast (the backend exception text never
   // crosses the WCF fault boundary, so the toast is the only user-visible failure channel).
   // guardBucketIds (CR-20260930-001): the buckets a DESIGN command touches. When any of them lies under a
   // combo that is under analysis, the command is not sent and the toast says why — the Shell would refuse
   // it anyway, but a refused command carries no text over the WCF fault boundary.
   private async run(command: any, failureMessage: string = 'Operation failed', guardBucketIds: string[] = []): Promise<boolean> {
      // Single choke point for most Collars commands (create/delete/move/override/settle/interest). Log the
      // op name on send and the real error on failure — the toast alone said nothing about the cause.
      const op = command && typeof command.getOperationName === 'function' ? command.getOperationName() : 'unknown';
      if (guardBucketIds.length > 0) {
         try {
            const reason = await this._analysisLocks.reasonFor(guardBucketIds, this._buckets);
            if (reason) {
               this._logger.info('Collars command refused by the analysis lock: ' + op, { guardBucketIds });
               this._toastr.error(reason);
               return false;
            }
         } catch (e) {
            // The lock read failing must not hide the command's own outcome: fall through and let the Shell decide.
            this._logger.warn('Collars analysis-lock read failed before ' + op, { error: e.stack || e });
         }
      }
      this._logger.debug('Collars command -> ' + op, command);
      try {
         await this._shellClient.processCommand(command);
         await this.reload();
         return true;
      } catch (e) {
         this._logger.error('Collars command failed: ' + op, { error: e.stack || e, op });
         this._toastr.error(failureMessage);
         return false;
      }
   }
}
